// client/src/App.tsx
import React, { useMemo, memo, useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import StarIcon from "@mui/icons-material/Star";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import type {
  PBVInsights,
  PBVRally,
  PBVSessionPackage,
  PBVShot,
} from "./pbvTypes";
import { useCurrentPBVEvent } from "./useCurrentPBVEvent";
import { PBVOverlay } from "./PBVOverlay";
import { useWorldToScreenFromCv } from "./useWorldToScreenFromCv";
// import { FinalRallyReportPopup } from "./FinalRallyReportDialog";
// import { FinalRallyWinnerAuditPopup } from "./FinaRallyWinnerAuditPopup";
// import { RallyPopup } from "./RallyPopup";
// import { RallyBoardPopup } from "./RallyBoardPopup";
import {
  DinkSvgIcon,
  GameStoryPanel,
  iconFastShot,
  iconReaction,
} from "./GameStoryDialog_UPDATED";
import PounceCatIcon from "./pounceCatIcon";
import { LeftRightCoverageIcon } from "./LeftRightCoverageIcon";
import { RallyPopup } from "./RallyPopup";
import { RallyBoardPopup } from "./RallyBoardPopup";
import { PbSkillsSolarPopup } from "./PBSkillsSolarPopup";
import { PackageSetScaffold } from "./GameStory_PackageSetScaffold";
import { PackageSetOverview } from "./GameStory_PackageSetOverview";
import { ExplorerNotesPopup } from "./GameStory_ExplorerNotesPopup";
import { DemoRunOverlay } from "./DemoRunOverlay";
import {
  GAME_STORY_FEATURES,
  GAME_STORY_FEATURES_UPDATED_AT,
} from "./GameStory_features";
import {
  CONTACT_EMAIL,
  COPYRIGHT_TEXT,
  CREATOR_NAME,
  PRODUCT_NAME_WITH_STAGE_PARENS,
} from "./branding";
// import { GameStoryDialog } from "./GameStoryDialog_UPDATED";

// import { PACKAGE_SETS, type PackageSetSummary } from "../../shared/packageSets";

type NoteworthyMoment = {
  momentType: string;
  timeSecClip: number;
  description: string;
  impactOnRally?: string;
  verdictForMoment?: string;
  refinementOrDrillForMoment?: string;
};

type PlayerAppearance = {
  shortLabel: string;
  longDescription?: string;
};

type PlayerAnalysis = {
  pbvPlayerId: number;
  isStrikerForPrimaryShot?: boolean;
  courtPosition?: string;
  verdictForPlayer?: string;
  refinementOrDrillFocusForPlayer?: string;
  noteworthyMoments?: NoteworthyMoment[];
  techniqueNotes?: TechniqueNote[];
  appearance?: PlayerAppearance;
};

type PrimaryShotAnalysis = {
  coachSummary?: string;
  errorsSummary?: string;
  pbvPlayerId?: number;
};

type RallyContextAnalysis = {
  verdict?: string;
  refinementOrDrillFocus?: string;
};

type ClipAnalysis = {
  clipId?: string;
  primaryShot?: PrimaryShotAnalysis;
  players?: PlayerAnalysis[];
  rallyContext?: RallyContextAnalysis;
};

type PackageSetSummary = {
  id: string;
  label: string;
  packageIds: string[];
};

const PACKAGE_SETS: PackageSetSummary[] = [
  {
    id: "brad_recent_mixed",
    label: "Brad – Recent mixed test set",
    packageIds: [
      "game_1_apr_6_2026",
      //"pro-training-session",
      //"alshon_wilson_garnett_loong_ppafinals",
      "brad_hunter_cara_goode_feb_16_2026",
      "feb_7_2026_brad_kelly_mike_michael",
      "brad_sam_trevor_mike_jan_20_2026",
      "brad_kelly_hartline_jan_17_2026",
      "brad_2_becky_mike_jan_17_2026",
      "brad_someone_becky_mikeK_jan_17_2026",
      "brad_henry_scott_kat_1_dec_30_2025",
      "brad_henry_scott_kat_2_dec_30_2025",
      "brad_lee_dec_27_2025",
      "dec_11_game",
      // "game_aug_2024",
    ],
  },
];

type PackageSummary = {
  id: string;
  label: string;
};

export type ClipNavigatorEntry = {
  clipId: string;
  rallyIndex: number;
  shotIndex: number;
  startSec: number;
  endSec: number;
  label?: string | undefined;
  insight?: {
    is_putaway?: boolean;
    is_reset?: boolean;
    is_final?: boolean;
    winning_team?: number | null | undefined;
    serving_team?: number;
    server_player_id?: number;
    running_score?: [number, number];
    point_awarded?: boolean | null | undefined;
    server_number?: number | null | undefined;
    point_awarded_to_team?: number | null | undefined;
    [key: string]: any;
  } & Record<string, any>;
};

type ClipStatus = "not_started" | "in_progress" | "done" | "error";

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const whole = Math.floor(seconds);
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

type MainTab =
  | "gameStory"
  | "video"
  | "rallyReport"
  | "rallyColumnReport"
  | "finalRallyReport"
  | "finalRallyAuditReport";

type AppViewMode = "single_package" | "package_group_scaffold";

type FeatureLaunchRequest = {
  key: string;
  nonce: number;
};

function featureLaunchIcon(launchKey?: string): React.ReactNode {
  switch (launchKey) {
    case "quick-triage":
      return <TrendingUpIcon sx={{ fontSize: 16 }} />;
    case "player-lens":
      return <SportsTennisIcon sx={{ fontSize: 16 }} />;
    case "pounce":
      return <PounceCatIcon size={18} />;
    case "dinks":
      return (
        <DinkSvgIcon size={18} color="rgba(255,255,255,0.88)" />
      );
    case "shot-quality":
      return <StarIcon sx={{ fontSize: 15, color: "rgba(235,200,90,0.95)" }} />;
    case "rally-quality":
      return <TrendingUpIcon sx={{ fontSize: 16 }} />;
    case "fast-shots":
      return iconFastShot(11);
    case "serve-return-risk":
    case "opportunity-finder":
      return <SportsTennisIcon sx={{ fontSize: 16 }} />;
    case "candidate-speedups":
      return <FlashOnIcon sx={{ fontSize: 16 }} />;
    case "possible-popups":
      return <FlashOnIcon sx={{ fontSize: 16, color: "#ef6c00" }} />;
    case "candidate-bodybags":
      return <FlashOnIcon sx={{ fontSize: 16, color: "#ce93d8" }} />;
    case "paddle-readiness":
    case "dink-readiness":
    case "atps":
    case "next-ball-reads":
      return <SportsTennisIcon sx={{ fontSize: 16 }} />;
    case "left-side-coverage":
      return <LeftRightCoverageIcon size={17} />;
    case "fast-reactions":
      return iconReaction(15);
    case "firefights":
      return <Box component="span">🔥</Box>;
    case "ai-history":
    case "full-game-ai":
      return <Box component="span">AI</Box>;
    case "add-your-game":
      return <OpenInNewIcon sx={{ fontSize: 15 }} />;
    default:
      return launchKey ? <OpenInNewIcon sx={{ fontSize: 15 }} /> : null;
  }
}

export const TITLE_NEXT_TIME_FOCUS = "Possible Focus for Next Game (WIP)";
export const TITLE_THREE_MINUTE_REVIEW = "3-Min Player Review (WIP)";
export const TITLE_POSITIONING_PATTERNS = "Positioning Patterns (WIP)";
export const TITLE_PLAYER_LENS_REVIEW = "Player Lens Review (WIP)";

export function App() {
  const [packageLoadingLabel, setPackageLoadingLabel] = useState<string>("");
  const [isPackageLoading, setIsPackageLoading] = useState(false);
  const [isGameStoryLoading, setIsGameStoryLoading] = useState(false);

  const [openExplorerNotes, setOpenExplorerNotes] = useState(false);
  const [openFeatureList, setOpenFeatureList] = useState(false);

  React.useEffect(() => {
    if (!openFeatureList || typeof document === "undefined") return;

    const body = document.body;
    const previousRecording = body.dataset.pbvDemoRecording;
    body.dataset.pbvDemoRecording = "true";

    return () => {
      if (previousRecording == null) {
        delete body.dataset.pbvDemoRecording;
      } else {
        body.dataset.pbvDemoRecording = previousRecording;
      }
    };
  }, [openFeatureList]);

  const [openQuickTriage, setOpenQuickTriage] = useState(false);
  const [openSingleTopFocus, setOpenSingleTopFocus] = useState(false);
  const [openThreeMinuteReview, setOpenThreeMinuteReview] = useState(false);
  const [openPlayerLensReview, setOpenPlayerLensReview] = useState(false);
  const [openPositioningPatterns, setOpenPositioningPatterns] = useState(false);

  const [gameStoryTopBanner, setGameStoryTopBanner] =
    React.useState<React.ReactNode>(null);

  const featureGroups = React.useMemo(() => {
    const groups = new Map<string, typeof GAME_STORY_FEATURES>();
    for (const feature of GAME_STORY_FEATURES) {
      const list = groups.get(feature.area) ?? [];
      list.push(feature);
      groups.set(feature.area, list);
    }
    return Array.from(groups.entries());
  }, []);

  const [activeTab, setActiveTab] = useState<MainTab>("gameStory");
  const [gameStoryFeatureLaunchRequest, setGameStoryFeatureLaunchRequest] =
    useState<FeatureLaunchRequest | null>(null);
  const [featureListPosition, setFeatureListPosition] = useState({
    x: 72,
    y: 72,
  });
  const [featureListSize, setFeatureListSize] = useState(() => ({
    width: typeof window === "undefined" ? 1440 : Math.min(1440, window.innerWidth - 56),
    height: typeof window === "undefined" ? 880 : Math.min(880, window.innerHeight - 64),
  }));
  const featureListDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const featureListResizeRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originWidth: number;
    originHeight: number;
  } | null>(null);

  const launchFeatureFromList = React.useCallback((launchKey?: string) => {
    if (!launchKey) return;
    if (typeof document !== "undefined") {
      delete document.body.dataset.pbvDemoFeatureFocus;
    }
    setActiveTab("gameStory");
    switch (launchKey) {
      case "quick-triage":
        setOpenQuickTriage(true);
        return;
      case "player-lens":
        setOpenPlayerLensReview(true);
        return;
      case "add-your-game":
        setOpenAddGameNote(true);
        return;
      default:
        setGameStoryFeatureLaunchRequest({
          key: launchKey,
          nonce: Date.now(),
        });
    }
  }, []);

  const beginFeatureListDrag = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("button, a, input, textarea, select")) return;
      featureListDragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: featureListPosition.x,
        originY: featureListPosition.y,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      event.preventDefault();
    },
    [featureListPosition.x, featureListPosition.y],
  );

  const moveFeatureListDrag = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const drag = featureListDragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const maxX =
        typeof window === "undefined"
          ? 72
          : Math.max(12, window.innerWidth - featureListSize.width - 12);
      const maxY =
        typeof window === "undefined"
          ? 72
          : Math.max(12, window.innerHeight - featureListSize.height - 12);
      setFeatureListPosition({
        x: Math.min(maxX, Math.max(12, drag.originX + event.clientX - drag.startX)),
        y: Math.min(maxY, Math.max(12, drag.originY + event.clientY - drag.startY)),
      });
    },
    [featureListSize.height, featureListSize.width],
  );

  const endFeatureListDrag = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (featureListDragRef.current?.pointerId === event.pointerId) {
        featureListDragRef.current = null;
      }
    },
    [],
  );
  const beginFeatureListResize = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      featureListResizeRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originWidth: featureListSize.width,
        originHeight: featureListSize.height,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      event.preventDefault();
      event.stopPropagation();
    },
    [featureListSize.height, featureListSize.width],
  );
  const moveFeatureListResize = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const resize = featureListResizeRef.current;
      if (!resize || resize.pointerId !== event.pointerId) return;
      const maxWidth =
        typeof window === "undefined" ? 1440 : window.innerWidth - featureListPosition.x - 12;
      const maxHeight =
        typeof window === "undefined" ? 880 : window.innerHeight - featureListPosition.y - 12;
      setFeatureListSize({
        width: Math.min(maxWidth, Math.max(560, resize.originWidth + event.clientX - resize.startX)),
        height: Math.min(maxHeight, Math.max(360, resize.originHeight + event.clientY - resize.startY)),
      });
    },
    [featureListPosition.x, featureListPosition.y],
  );
  const endFeatureListResize = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (featureListResizeRef.current?.pointerId === event.pointerId) {
        featureListResizeRef.current = null;
      }
    },
    [],
  );
  const filterToggleSx = {
    color: "rgba(255,255,255,0.85)",
    borderColor: "rgba(255,255,255,0.22)",
    backgroundColor: "rgba(255,255,255,0.06)",
    "&.Mui-selected": {
      backgroundColor: "rgba(255,255,255,0.14)",
      borderColor: "rgba(255,255,255,0.38)",
      color: "rgba(255,255,255,0.95)",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "rgba(255,255,255,0.18)",
    },
    textTransform: "none",
  };

  // Replace your openX functions with “go to tab”
  const goTab = (tab: MainTab, pkgId?: string) => {
    if (pkgId) setSelectedId(pkgId);
    setActiveTab(tab);
  };

  const [openWhatIsThis, setOpenWhatIsThis] = useState(false);
  const [openAddGameNote, setOpenAddGameNote] = useState(false);

  const [openSolar, setOpenSolar] = useState(false);
  const [durationSec, setDurationSec] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  const [packages, setPackages] = useState<PackageSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");

  const [viewMode, setViewMode] = useState<AppViewMode>("single_package");
  const [selectedPackageSetId, setselectedPackageSetId] = useState<string>(
    PACKAGE_SETS[0]?.id ?? "",
  );

  const [finalRallyOpen, setFinalRallyOpen] = useState(false);
  const [finalRallyWinnerAuditOpen, setRallyWinnerAuditOpen] = useState(false);
  const [kitchenArrivalOpen, setKitchenArrival] = useState(false);
  const [rallyColumnOpen, setRallyColumnOpen] = useState(false);
  const [gameStoryOpen, setGameStoryOpen] = useState(false);

  const [pbvPackage, setPbvPackage] = useState<PBVSessionPackage | null>(null);
  const [currentTimeSec, setCurrentTimeSec] = useState<number>(0);

  const [clips, setClips] = useState<ClipNavigatorEntry[]>([]);
  const [activeClip, setActiveClip] = useState<ClipNavigatorEntry | null>(null);
  const [isClipLooping, setIsClipLooping] = useState<boolean>(false);

  const [clipStatusById, setClipStatusById] = useState<
    Record<string, ClipStatus>
  >({});

  const [analysisByClipId, setAnalysisByClipId] = useState<
    Record<string, ClipAnalysis | null>
  >({});

  const [finalClipStatusById, setFinalClipStatusById] = useState<
    Record<string, ClipStatus>
  >({});

  const [finalAnalysisByClipId, setFinalAnalysisByClipId] = useState<
    Record<string, ClipAnalysis | null>
  >({});

  const [canonicalAppearanceById, setCanonicalAppearanceById] = useState<
    Record<number, PlayerAppearance>
  >({});

  const [clipFilter, setClipFilter] = useState<"all" | "final_only">(
    "final_only",
  );

  const [playbackRate, setPlaybackRate] = useState(1);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const packageLoadSeqRef = useRef(0);

  const openGameStory = (pkgId: string) => {
    setSelectedId(pkgId);
    setGameStoryOpen(true);
  };

  // example open action (use whatever you currently do to set selectedId)
  const openFinalRally = (pkgId: string) => {
    setSelectedId(pkgId);
    setFinalRallyOpen(true);
  };

  const openFinalRallyAudit = (pkgId: string) => {
    setSelectedId(pkgId);
    setRallyWinnerAuditOpen(true);
  };

  const openKitchenArrival = (pkgId: string) => {
    setSelectedId(pkgId);
    setKitchenArrival(true);
  };

  const openRallyColumnOpen = (pkgId: string) => {
    setSelectedId(pkgId);
    setRallyColumnOpen(true);
  };

  const handlePlaybackRateChange = (rate: number) => {
    setPlaybackRate(rate);
    if (videoRef.current) videoRef.current.playbackRate = rate;
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const nudgeVideoSeconds = React.useCallback(
    ({ deltaSec }: { deltaSec: number }) => {
      const video = videoRef.current;
      if (!video) return;

      const duration = video.duration || durationSec || 0;
      if (duration <= 0) return;

      const nextTime = Math.min(
        Math.max(0, video.currentTime + deltaSec),
        duration,
      );
      video.currentTime = nextTime;
      setCurrentTimeSec(nextTime);
    },
    [durationSec],
  );

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        nudgeVideoSeconds({ deltaSec: -0.1 });
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        nudgeVideoSeconds({ deltaSec: 0.1 });
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nudgeVideoSeconds]);

  const selectedPackageGroup = useMemo(
    () =>
      PACKAGE_SETS.find((group) => group.id === selectedPackageSetId) ?? null,
    [selectedPackageSetId],
  );

  const packageGroupPackages = useMemo(() => {
    if (!selectedPackageGroup) return [];
    const allowed = new Set(selectedPackageGroup.packageIds);
    return packages.filter((pkg) => allowed.has(pkg.id));
  }, [packages, selectedPackageGroup]);

  const finalCount = React.useMemo(
    () => clips.filter((c) => c.insight?.is_final).length,
    [clips],
  );

  const visibleClips = React.useMemo(
    () =>
      clipFilter === "final_only"
        ? clips.filter((c) => c.insight?.is_final)
        : clips,
    [clips, clipFilter],
  );

  function handleSelectPackageGroup(nextGroupId: string) {
    setselectedPackageSetId(nextGroupId);

    const nextGroup = PACKAGE_SETS.find((g) => g.id === nextGroupId);
    if (!nextGroup) return;

    const nextPackageId =
      nextGroup.packageIds.find((id) =>
        packages.some((pkg) => pkg.id === id),
      ) ?? "";

    if (!nextPackageId) return;

    // only change selected game if current one is not in the new set
    const currentStillInSet = nextGroup.packageIds.includes(selectedId);

    if (!currentStillInSet) {
      setSelectedId(nextPackageId);
    }
  }

  useEffect(() => {
    // async function fetchPackages() {
    //   try {
    //     const res = await fetch("/api/packages");
    //     if (!res.ok) {
    //       throw new Error(`Failed to fetch packages: ${res.statusText}`);
    //     }
    //     const data = (await res.json()) as PackageSummary[];
    //     setPackages(data);
    //     if (data.length > 0) {
    //       setSelectedId(data[0].id);
    //       if (data[0].id) {
    //         handleLoadPackage({ id: data[0].id });
    //       }
    //     }
    //   } catch (err) {
    //     console.error(err);
    //   }
    // }

    async function fetchPackages() {
      try {
        const res = await fetch("/api/packages");
        if (!res.ok) {
          throw new Error(`Failed to fetch packages: ${res.statusText}`);
        }

        const data = (await res.json()) as PackageSummary[];
        setPackages(data);

        // const initialGroup =
        //   PACKAGE_SETS.find((g) => g.id === (PACKAGE_SETS[0]?.id ?? "")) ??
        //   null;

        const requestedPackageId = new URLSearchParams(window.location.search).get("packageId");
        const initialId =
          data.find((pkg) => pkg.id === requestedPackageId)?.id ?? data[0]?.id;

// // initialGroup?.packageIds.find((id) =>
// //   data.some((pkg) => pkg.id === id),
// // ) ??
// // data[0]?.id ??
// // "";

// console.error({initialId, data});

        if (initialId) {
          setSelectedId(initialId);
          void handleLoadPackage({ id: initialId });
        }
      } catch (err) {
// console.error(err);
      }
    }

    fetchPackages();
  }, []);

  function updateCanonicalAppearances(
    analysis: ClipAnalysis | null | undefined,
  ) {
    if (!analysis?.players || !analysis.players.length) return;

    setCanonicalAppearanceById((prev) => {
      const next = { ...prev };
      for (const player of analysis.players ?? []) {
        const id = player.pbvPlayerId;
        const appearance = player.appearance;
        if (typeof id === "number" && appearance?.shortLabel && !next[id]) {
          next[id] = appearance;
        }
      }
      return next;
    });
  }

  function syncClipsAndStatuses(nextClips: ClipNavigatorEntry[]) {
    setClips(nextClips);

    setClipStatusById((prev) => {
      const next: Record<string, ClipStatus> = {};
      for (const clip of nextClips)
        next[clip.clipId] = prev[clip.clipId] ?? "not_started";
      return next;
    });

    setFinalClipStatusById((prev) => {
      const next: Record<string, ClipStatus> = {};
      for (const clip of nextClips)
        next[clip.clipId] = prev[clip.clipId] ?? "not_started";
      return next;
    });

    setActiveClip(null);
    setIsClipLooping(false);
  }

  async function refreshExistingAnalysisStatus(
    packageId: string,
    clipList: ClipNavigatorEntry[],
  ) {
    if (!packageId || clipList.length === 0) return;

    try {
      const res = await fetch("/api/clip-statuses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          packageId,
          clipIds: clipList.map((c) => c.clipId),
        }),
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch clip statuses: ${res.statusText}`);
      }

      const data = (await res.json()) as {
        statuses: Record<string, "done" | "not_started">;
      };

      setClipStatusById((prev) => {
        const next: Record<string, ClipStatus> = { ...prev };
        for (const [clipId, status] of Object.entries(data.statuses)) {
          const current = next[clipId];
          if (!current || current === "not_started") {
            next[clipId] = status;
          }
        }
        return next;
      });
    } catch (err) {
// console.error(err);
    }
  }

  async function loadClipsForPackage(
    id: string,
    opts?: {
      silent?: boolean;
      forceRebuild?: boolean;
      paddingBeforeSec?: number;
      paddingAfterSec?: number;
    },
  ) {
    const {
      silent = false,
      forceRebuild = false,
      paddingBeforeSec = 0.8,
      paddingAfterSec = 0.8,
    } = opts ?? {};
    try {
      const res = await fetch(`/api/packages/${id}/clips`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paddingBeforeSec,
          paddingAfterSec,
          forceRebuild,
        }),
      });

      if (!res.ok) {
        throw new Error(
          `Failed to load/generate clips: ${res.status} ${res.statusText}`,
        );
      }

      const data = (await res.json()) as {
        packageId: string;
        clips?: ClipNavigatorEntry[];
      };

      const nextClips = data.clips ?? [];
      syncClipsAndStatuses(nextClips);

      void refreshExistingAnalysisStatus(id, nextClips);

      if (!silent) {
        const count = nextClips.length;
        alert(`Loaded/generated ${count} clips for package ${data.packageId}.`);
      }
    } catch (err) {
// console.error(err);
      if (!silent) {
        alert(
          err instanceof Error ? err.message : "Failed to load/generate clips.",
        );
      }
    }
  }

  async function loadCvForPackageInBackground(id: string, loadSeq: number) {
    try {
      const res = await fetch(`/api/package/${id}/cv`);
      if (!res.ok) {
        throw new Error(`Failed to load package CV: ${res.statusText}`);
      }

      const data = (await res.json()) as Pick<PBVSessionPackage, "id" | "pbvCv">;

      if (packageLoadSeqRef.current !== loadSeq) return;

      setPbvPackage((prev) => {
        if (!prev || prev.id !== id) return prev;
        return { ...prev, pbvCv: data.pbvCv ?? null };
      });
    } catch (err) {
// console.error(err);
    }
  }

  async function handleLoadPackage({ id }: { id: string }) {
    if (!id) return;
    const loadSeq = packageLoadSeqRef.current + 1;
    packageLoadSeqRef.current = loadSeq;

    const nextLabel = packages.find((pkg) => pkg.id === id)?.label ?? id;

    setIsPackageLoading(true);
    setPackageLoadingLabel(nextLabel);

    try {
      const res = await fetch(`/api/package/${id}?includeCv=0`);
      if (!res.ok) {
        throw new Error(`Failed to load package: ${res.statusText}`);
      }
      const data = (await res.json()) as PBVSessionPackage;
      const packageWithoutCv = { ...data, pbvCv: null };

      setPbvPackage(packageWithoutCv);
      setCurrentTimeSec(0);
      setDurationSec(0);
      setIsPlaying(false);

      setClips([]);
      setActiveClip(null);
      setIsClipLooping(false);

      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.load();
      }

      if (activeTab !== "gameStory") {
        void loadCvForPackageInBackground(id, loadSeq);
      }
      void loadClipsForPackage(id, { silent: true });
    } catch (err) {
// console.error(err);
      alert(err instanceof Error ? err.message : "Failed to load package.");
    } finally {
      setIsPackageLoading(false);
    }
  }

  async function handleLoadPackage_old({ id }: { id: string }) {
    if (!id) return;
    try {
      const res = await fetch(`/api/package/${id}`);
      if (!res.ok) {
        throw new Error(`Failed to load package: ${res.statusText}`);
      }
      const data = (await res.json()) as PBVSessionPackage;

      setPbvPackage(data);
      setCurrentTimeSec(0);
      setDurationSec(0);
      setIsPlaying(false);

      setClips([]);
      setActiveClip(null);
      setIsClipLooping(false);

      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.load();
      }

      await loadClipsForPackage(id, { silent: true });
    } catch (err) {
// console.error(err);
      alert(err instanceof Error ? err.message : "Failed to load package.");
    }
  }

  async function handleGenerateClips() {
    if (!selectedId) {
      alert("No package selected.");
      return;
    }

    await loadClipsForPackage(selectedId, {
      silent: false,
      forceRebuild: true,
      paddingBeforeSec: 0.8,
      paddingAfterSec: 0.8,
    });
  }

  // async function handleAnalyzeFinalRally(clip: ClipNavigatorEntry) {
  //   setFinalClipStatusById((prev) => ({
  //     ...prev,
  //     [clip.clipId]: "in_progress",
  //   }));

  //   try {
  //     if (!selectedId) throw new Error("No package selected");

  //     const res = await fetch("/api/analyze-final-rally", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({ packageId: selectedId, clip }),
  //     });

  //     if (!res.ok) {
  //       throw new Error(
  //         `Final rally analyze failed: ${res.status} ${res.statusText}`,
  //       );
  //     }

  //     const analysisResponse = await res.json();
  //     const clipAnalysis: ClipAnalysis =
  //       analysisResponse.analysis ?? analysisResponse;

  //     updateCanonicalAppearances(clipAnalysis);

  //     setFinalAnalysisByClipId((prev) => ({
  //       ...prev,
  //       [clip.clipId]: clipAnalysis,
  //     }));

  //     setFinalClipStatusById((prev) => ({
  //       ...prev,
  //       [clip.clipId]: "done",
  //     }));
  //   } catch (err) {
  //     console.error(err);
  //     setFinalClipStatusById((prev) => ({
  //       ...prev,
  //       [clip.clipId]: "error",
  //     }));
  //   }
  // }

  // async function handleAnalyzeClip(clip: ClipNavigatorEntry) {
  //   setClipStatusById((prev) => ({
  //     ...prev,
  //     [clip.clipId]: "in_progress",
  //   }));

  //   try {
  //     if (!selectedId) {
  //       throw new Error("No package selected");
  //     }

  //     const res = await fetch("/api/analyze-clip", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({
  //         packageId: selectedId,
  //         clip,
  //       }),
  //     });

  //     if (!res.ok) {
  //       throw new Error(`Analyze failed: ${res.status} ${res.statusText}`);
  //     }

  //     const analysisJson = await res.json();
  //     const clipAnalysis: ClipAnalysis = analysisJson.analysis ?? analysisJson;

  //     updateCanonicalAppearances(clipAnalysis);

  //     setAnalysisByClipId((prev) => ({
  //       ...prev,
  //       [clip.clipId]: clipAnalysis,
  //     }));

  //     setClipStatusById((prev) => ({
  //       ...prev,
  //       [clip.clipId]: "done",
  //     }));
  //   } catch (err) {
  //     console.error(err);
  //     setClipStatusById((prev) => ({
  //       ...prev,
  //       [clip.clipId]: "error",
  //     }));
  //   }
  // }

  // function handleSelectClip(clip: ClipNavigatorEntry) {
  //   const video = videoRef.current;
  //   if (!video) return;

  //   setActiveClip(clip);
  //   setIsClipLooping(true);

  //   const status = clipStatusById[clip.clipId];
  //   const finalStatus = finalClipStatusById[clip.clipId];

  //   if (status === "done" && !analysisByClipId[clip.clipId]) {
  //     (async () => {
  //       try {
  //         if (!selectedId) return;
  //         const res = await fetch("/api/analyze-clip", {
  //           method: "POST",
  //           headers: { "Content-Type": "application/json" },
  //           body: JSON.stringify({
  //             packageId: selectedId,
  //             clip,
  //           }),
  //         });
  //         if (!res.ok) {
  //           throw new Error(
  //             `Analyze (cached) failed: ${res.status} ${res.statusText}`,
  //           );
  //         }
  //         const analysisResponse = await res.json();
  //         const clipAnalysis: ClipAnalysis =
  //           analysisResponse.analysis ?? analysisResponse;

  //         updateCanonicalAppearances(clipAnalysis);

  //         setAnalysisByClipId((prev) => ({
  //           ...prev,
  //           [clip.clipId]: clipAnalysis,
  //         }));
  //       } catch (err) {
  //         console.error(
  //           "Failed to load cached analysis for clip",
  //           clip.clipId,
  //           err,
  //         );
  //       }
  //     })();
  //   }

  //   if (
  //     clip.insight?.is_final &&
  //     finalStatus === "done" &&
  //     !finalAnalysisByClipId[clip.clipId]
  //   ) {
  //     (async () => {
  //       try {
  //         if (!selectedId) return;
  //         const res = await fetch("/api/analyze-final-rally", {
  //           method: "POST",
  //           headers: { "Content-Type": "application/json" },
  //           body: JSON.stringify({ packageId: selectedId, clip }),
  //         });
  //         if (!res.ok)
  //           throw new Error(
  //             `Final rally (cached) failed: ${res.status} ${res.statusText}`,
  //           );
  //         const analysisResponse = await res.json();
  //         const clipAnalysis: ClipAnalysis =
  //           analysisResponse.analysis ?? analysisResponse;

  //         updateCanonicalAppearances(clipAnalysis);

  //         setFinalAnalysisByClipId((prev) => ({
  //           ...prev,
  //           [clip.clipId]: clipAnalysis,
  //         }));
  //       } catch (err) {
  //         console.error(
  //           "Failed to load cached final-rally analysis for clip",
  //           clip.clipId,
  //           err,
  //         );
  //       }
  //     })();
  //   }

  //   video.currentTime = clip.startSec;
  //   void video.play();
  //   setIsPlaying(true);
  // }

  const { currentMs, currentRally, currentShot } = useCurrentPBVEvent({
    currentTimeSec,
    insights: pbvPackage?.pbvInsights ?? null,
  });

  type ShotDetailRow = { label: string; value: string };

  const currentShotDetails: ShotDetailRow[] = [];
  const shotAny = currentShot as any;
  const rallyAny = currentRally as any;

  if (shotAny) {
    if (typeof currentMs === "number") {
      const rallyStart =
        rallyAny && typeof rallyAny.start_ms === "number"
          ? rallyAny.start_ms
          : null;
      const rallyOffsetMs = rallyStart != null ? currentMs - rallyStart : null;

      currentShotDetails.push({
        label: "Time",
        value:
          rallyOffsetMs != null
            ? `${(currentMs / 1000).toFixed(2)}s (rally +${(
                rallyOffsetMs / 1000
              ).toFixed(2)}s)`
            : `${(currentMs / 1000).toFixed(2)}s`,
      });
    }

    if (shotAny.player_id != null || shotAny.team_index != null) {
      currentShotDetails.push({
        label: "Player",
        value: [
          shotAny.player_id != null ? `P${shotAny.player_id}` : null,
          shotAny.team_index != null ? `team ${shotAny.team_index}` : null,
        ]
          .filter(Boolean)
          .join(" · "),
      });
    }

    if (
      shotAny.stroke_side ||
      shotAny.stroke_type ||
      shotAny.vertical_type ||
      shotAny.swing_shape_type
    ) {
      currentShotDetails.push({
        label: "Stroke",
        value: [
          shotAny.stroke_side,
          shotAny.stroke_type,
          shotAny.vertical_type,
          shotAny.swing_shape_type,
        ]
          .filter(Boolean)
          .join(" · "),
      });
    }

    if (
      shotAny.ball_height_type ||
      shotAny.ball_speed_type ||
      shotAny.ball_spin_type ||
      shotAny.depth_type ||
      shotAny.net_clearance_type
    ) {
      currentShotDetails.push({
        label: "Ball flight",
        value: [
          shotAny.ball_height_type,
          shotAny.ball_speed_type,
          shotAny.ball_spin_type,
          shotAny.depth_type,
          shotAny.net_clearance_type,
        ]
          .filter(Boolean)
          .join(" · "),
      });
    }

    const advBits: string[] = [];
    if (shotAny.advantage_scale != null) {
      advBits.push(`scale ${shotAny.advantage_scale}`);
    }
    if (shotAny.advantage_at_contact != null) {
      advBits.push(`at contact ${shotAny.advantage_at_contact}`);
    }
    if (shotAny.dynamic_advantage != null) {
      advBits.push(`dynamic ${shotAny.dynamic_advantage}`);
    }
    if (advBits.length) {
      currentShotDetails.push({
        label: "Advantage",
        value: advBits.join(" · "),
      });
    }

    if (
      shotAny.pressure_type ||
      shotAny.tactical_pattern_type ||
      shotAny.tactical_intent_type
    ) {
      currentShotDetails.push({
        label: "Tactics",
        value: [
          shotAny.pressure_type,
          shotAny.tactical_pattern_type,
          shotAny.tactical_intent_type,
        ]
          .filter(Boolean)
          .join(" · "),
      });
    }

    if (
      shotAny.shot_result ||
      shotAny.mistake_type ||
      shotAny.rally_outcome_type ||
      shotAny.is_final
    ) {
      const bits: string[] = [];
      if (shotAny.shot_result) bits.push(shotAny.shot_result);
      if (shotAny.mistake_type) bits.push(shotAny.mistake_type);
      if (shotAny.rally_outcome_type) bits.push(shotAny.rally_outcome_type);
      if (shotAny.is_final) bits.push("final shot in rally");
      currentShotDetails.push({
        label: "Outcome",
        value: bits.join(" · "),
      });
    }

    if (shotAny.tags) {
      const tagKeys = Object.keys(shotAny.tags);
      if (tagKeys.length) {
        currentShotDetails.push({
          label: "Tags",
          value: `${tagKeys.length} tag${
            tagKeys.length === 1 ? "" : "s"
          } (microtips + metadata)`,
        });
      }
    }
  }

  function playPause() {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      void video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  }

  function handleScrubChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const video = videoRef.current;
    if (!video) return;

    const nextTime = Number(event.target.value);
    video.currentTime = nextTime;
    setCurrentTimeSec(nextTime);
  }

  const fraction =
    durationSec > 0
      ? Math.min(Math.max(currentTimeSec / durationSec, 0), 1)
      : 0;

  // const activeClipAnalysisRaw = activeClip
  //   ? analysisByClipId[activeClip.clipId] ?? null
  //   : null;

  const activeClipAnalysisRaw = activeClip
    ? activeClip.insight?.is_final
      ? (finalAnalysisByClipId[activeClip.clipId] ??
        analysisByClipId[activeClip.clipId] ??
        null)
      : (analysisByClipId[activeClip.clipId] ?? null)
    : null;

  const activeClipAnalysis = React.useMemo(() => {
    if (!activeClipAnalysisRaw) return null;
    if (!Object.keys(canonicalAppearanceById).length) {
      return activeClipAnalysisRaw;
    }

    const players = activeClipAnalysisRaw.players?.map((p) => {
      const id = p.pbvPlayerId;
      const canonical =
        typeof id === "number" ? canonicalAppearanceById[id] : undefined;
      return canonical && canonical.shortLabel
        ? { ...p, appearance: canonical }
        : p;
    });

    return { ...activeClipAnalysisRaw, players };
  }, [activeClipAnalysisRaw, canonicalAppearanceById]);

  const isWithinActiveClip =
    !!activeClip &&
    currentTimeSec >= activeClip.startSec &&
    currentTimeSec <= activeClip.endSec;

  const activeClipTimeSec =
    activeClip && isWithinActiveClip ? currentTimeSec - activeClip.startSec : 0;

  const worldToScreen = useWorldToScreenFromCv(pbvPackage?.pbvCv ?? null);

const topActionPrimarySx = {
  textTransform: "none",
  letterSpacing: "0.01em",
  ml: 1,
  px: 2.1,
  py: 0.95,
  minHeight: 42,
  borderRadius: 1.4,
  fontWeight: 850,
  fontSize: 14,
  background: "linear-gradient(180deg,#ffe082,#f6c343)",
  color: "#1a1a1a",
  border: "1px solid rgba(255,215,120,0.65)",
  boxShadow: "0 0 0 1px rgba(0,0,0,0.25), 0 3px 10px rgba(0,0,0,0.35)",
  "&:hover": {
    background: "linear-gradient(180deg,#ffe58f,#f9cf5a)",
  },
  "body[data-pbv-demo-recording='true'] &:focus, body[data-pbv-demo-recording='true'] &:focus-visible, body[data-pbv-demo-recording='true'] &.Mui-focusVisible": {
    outline: "none",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.25), 0 3px 10px rgba(0,0,0,0.35)",
  },
};

const topActionSecondarySx = {
  textTransform: "none",
  letterSpacing: "0.01em",
  ml: 0.75,
  px: 1.9,
  py: 0.8,
  minHeight: 42,
  borderRadius: 1.35,
  fontWeight: 740,
  fontSize: 13.5,
  color: "rgba(255,255,255,0.92)",
  background: "linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.05))",
  border: "1px solid rgba(255,255,255,0.16)",
  boxShadow: "0 1px 6px rgba(0,0,0,0.22)",
  "&:hover": {
    background: "linear-gradient(180deg, rgba(255,255,255,0.13), rgba(255,255,255,0.07))",
    borderColor: "rgba(255,255,255,0.24)",
    boxShadow: "0 2px 9px rgba(0,0,0,0.28)",
  },
};

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          minHeight: "100vh",
          bgcolor: "#101010",
          color: "#f5f5f5",
          padding: 2,
          boxSizing: "border-box",
        }}
      >
        {isPackageLoading || isGameStoryLoading ? (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 2000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.52)",
            }}
          >
            <Box
              sx={{
                minWidth: 320,
                maxWidth: 520,
                px: 3,
                py: 2.25,
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,0.16)",
                backgroundColor: "#171717",
                boxShadow: "0 18px 60px rgba(0,0,0,0.42)",
              }}
            >
              <Box
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#ffffff",
                  mb: 0.75,
                }}
              >
                Loading game...
              </Box>

              <Box
                sx={{
                  fontSize: 14,
                  color: "rgba(255,255,255,0.78)",
                  mb: 1.25,
                  wordBreak: "break-word",
                }}
              >
                {packageLoadingLabel}
              </Box>

              <Box
                sx={{
                  height: 6,
                  borderRadius: 999,
                  overflow: "hidden",
                  backgroundColor: "rgba(255,255,255,0.08)",
                }}
              >
                <Box
                  sx={{
                    width: "38%",
                    height: "100%",
                    borderRadius: 999,
                    background:
                      "linear-gradient(90deg, rgba(255,255,255,0.45), rgba(255,255,255,0.92))",
                    animation: "packageLoadPulse 1.1s ease-in-out infinite",
                    "@keyframes packageLoadPulse": {
                      "0%": { transform: "translateX(-110%)" },
                      "100%": { transform: "translateX(360%)" },
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        ) : null}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 0.22,
            px: 0,
            minHeight: 32,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              minWidth: 0,
              flex: "1 1 auto",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Box
              component="img"
              src="/pickleball_no_logo.png"
              alt="Pickleball"
              sx={{
                width: 24,
                height: 24,
                display: "block",
                objectFit: "contain",
                flex: "0 0 auto",
                opacity: 0.95,
              }}
            />

            <Box
              sx={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: "rgba(255,255,255,0.93)",
                letterSpacing: "0.01em",
                lineHeight: 1.1,
              }}
            >
              <Box
                component="span"
                data-demo="app-title"
                sx={{ fontWeight: 700, fontSize: 18 }}
              >
                {PRODUCT_NAME_WITH_STAGE_PARENS}
              </Box>
              {/* <Box
                component="span"
                sx={{
                  ml: 1.1,
                  fontWeight: 400,
                  fontSize: 14.5,
                  //color: "rgba(255,255,255,0.62)",
                }}
              >
                — Exploring the structure of a pickleball game
              </Box> */}
              <Box
                component="span"
                sx={{
                  display: "none",
                  ml: 1.1,
                  fontWeight: 900,
                  fontSize: 16,
                  color: "white",
                  fontStyle: "italic",
                  "@media (max-width: 1699px)": {
                    display: "inline",
                  },
                }}
              >
                Best on device with screen width at least 1700px for now
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.95,
              flex: "0 0 auto",
              whiteSpace: "nowrap",
              ml: 2,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => setOpenWhatIsThis(true)}
              sx={{
                ...filterToggleSx,
                height: 28,
                px: 1.0,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                whiteSpace: "nowrap",
                textTransform: "none",
                lineHeight: 1,
                borderColor: "rgba(255,255,255,0.18)",
                backgroundColor: "rgba(255,255,255,0.03)",
              }}
            >
              What is this?
            </Button>

            <Button
              size="small"
              variant="contained"
              data-demo="add-your-game-button"
              data-happy-path="add-your-game-button"
              onClick={() => setOpenAddGameNote(true)}
              sx={{
                height: 28,
                px: 1.2,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                whiteSpace: "nowrap",
                textTransform: "none",
                lineHeight: 1,
                borderRadius: 1,
                fontWeight: 700,
                letterSpacing: 0,
                color: "#111111",
                bgcolor: "#f7d76b",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#ffe38a",
                  boxShadow: "none",
                },
              }}
            >
              Add your game
            </Button>

            {/* <Button
  size="small"
  variant="outlined"
  onClick={() => setOpenExplorerNotes(true)}
  sx={{
    ...filterToggleSx,
    height: 28,
    px: 1.0,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    whiteSpace: "nowrap",
    textTransform: "none",
    lineHeight: 1,
    borderColor: "rgba(255,255,255,0.18)",
    backgroundColor: "rgba(255,255,255,0.03)",
  }}
>
  Explorer Notes
</Button> */}

            <Button
              size="small"
              variant="outlined"
              data-happy-path="open-pickleball-universe"
              onClick={() => setOpenSolar(true)}
              sx={{
                ...filterToggleSx,
                height: 28,
                px: 1.0,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                whiteSpace: "nowrap",
                textTransform: "none",
                lineHeight: 1,
                borderColor: "rgba(255,255,255,0.18)",
                backgroundColor: "rgba(255,255,255,0.03)",
              }}
            >
              See Pickleball Universe
            </Button>

            <Box
              sx={{
                color: "white",
                whiteSpace: "nowrap",
                fontSize: 15,
                lineHeight: 1,
              }}
            >
              <Box component="span" sx={{ mr: "6px" }}>
                {COPYRIGHT_TEXT}
              </Box>
              <a style={{ color: "white" }} href={`mailto:${CONTACT_EMAIL}`}>
                {CREATOR_NAME}
              </a>
            </Box>
          </Box>

          {/* <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.95,
              flex: "0 0 auto",
              whiteSpace: "nowrap",
              ml: 2,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => setOpenSolar(true)}
              sx={{
                ...filterToggleSx,
                height: 28,
                px: 1.0,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                whiteSpace: "nowrap",
                textTransform: "none",
                lineHeight: 1,
                borderColor: "rgba(255,255,255,0.18)",
                backgroundColor: "rgba(255,255,255,0.03)",
              }}
            >
              See Pickleball Universe
            </Button>

            <Box
              sx={{
                color: "white",
                whiteSpace: "nowrap",
                fontSize: 15,
                lineHeight: 1,
              }}
            >
              <Box component="span" sx={{ mr: "6px" }}>
                {COPYRIGHT_TEXT}
              </Box>
              <a style={{ color: "white" }} href={`mailto:${CONTACT_EMAIL}`}>
                {CREATOR_NAME}
              </a>
            </Box>
          </Box> */}
          {/* <Box
            sx={{
              color: "white",
              flex: "0 0 auto",
              whiteSpace: "nowrap",
              fontSize: 16,
              // opacity: 0.92,
            }}
          >
            <Box component="span" sx={{ mr: "6px" }}>
              © 2026 Pickleball Game Explorer
            </Box>
            <a style={{ color: "white" }} href="mailto:bradflyon@gmail.com">
              Brad Lyon
            </a>
          </Box> */}
        </Box>

        {/* new stuff Feb 7, 2026 */}
        <>
          <Box
            sx={{
              minHeight: "100vh",
              bgcolor: "#101010",
              color: "#f5f5f5",
              padding: 2,
              boxSizing: "border-box",
            }}
          >
            {/* Top bar */}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1.25,
                mb: 0.35,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1.25,
                  alignItems: "center",
                  minWidth: 0,
                  flexWrap: "wrap",
                }}
              >
                {/* <PackageSetScaffold
                  packageGroups={PACKAGE_SETS}
                  selectedPackageGroupId={selectedPackageSetId}
                  onSelectPackageGroup={handleSelectPackageGroup}
                  viewMode={viewMode}
                  onChangeViewMode={setViewMode}
                  filterToggleSx={filterToggleSx}
                /> */}

                <Box sx={{ lineHeight: 1, opacity: 0.72 }}>Select game:</Box>

                <Select
                  value={selectedId}
                  size="small"
                  onChange={(event: any) => {
                    const nextId = event.target.value;
                    setSelectedId(nextId);
                    if (nextId) void handleLoadPackage({ id: nextId });
                  }}
                  sx={{
                    minWidth: 250,
                    height: 32,
                    color: "rgba(255,255,255,0.9)",
                    bgcolor: "rgba(255,255,255,0.06)",
                    borderRadius: 1,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.14)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.22)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.24)",
                    },
                    "& .MuiSelect-icon": {
                      color: "rgba(255,255,255,0.55)",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        "& .MuiMenuItem-root": {
                          fontSize: 14,
                          minHeight: 28,
                          py: 0,
                        },
                      },
                    },
                  }}
                >
                  {packages.map((pkg) => (
                    <MenuItem
                      key={pkg.id}
                      value={pkg.id}
                      sx={{ color: "#000000" }}
                    >
                      {pkg.label}
                    </MenuItem>
                  ))}
                </Select>

                {activeTab === "gameStory" && viewMode === "single_package"
                  ? gameStoryTopBanner
                  : null}

                {activeTab === "gameStory" &&
                gameStoryTopBanner &&
                viewMode === "single_package" ? (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      data-demo="features-button"
                      onClick={() => setOpenFeatureList(true)}
                      sx={topActionPrimarySx}
                    >
                      Features
                    </Button>
                    <Button
                      variant="contained"
                      data-happy-path="gs-open-start-here"
                      onClick={() => setOpenQuickTriage(true)}
                      sx={topActionPrimarySx}
                    >
                      3-Min Triage (WIP)
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      data-happy-path="gs-open-player-lens"
                      onClick={() => setOpenPlayerLensReview(true)}
                      sx={topActionPrimarySx}
                    >
                      {TITLE_PLAYER_LENS_REVIEW}
                    </Button>
                    {/* <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setOpenSingleTopFocus(true)}
                      sx={topActionPrimarySx} //  topActionSecondarySx}
                      >
                      {TITLE_NEXT_TIME_FOCUS}
                    </Button> */}
                    {/* <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setOpenThreeMinuteReview(true)}
                      sx={topActionPrimarySx}
                    >
                      {TITLE_THREE_MINUTE_REVIEW}
                    </Button> */}
                    {/* <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setOpenPositioningPatterns(true)}
                      sx={topActionPrimarySx}
                    >
                      {TITLE_POSITIONING_PATTERNS}
                    </Button> */}
                  </>
                ) : null}

                {/* <Button
                  onClick={() => handleLoadPackage({ id: selectedId })}
                  sx={{
                    px: 1.7,
                    minWidth: 0,
                    height: 32,
                    borderRadius: 1,
                    textTransform: "none",
                    fontWeight: 500,
                    letterSpacing: 0,
                    color: "rgba(255,255,255,0.9)",
                    bgcolor: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.14)",
                    boxShadow: "none",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.10)",
                      borderColor: "rgba(255,255,255,0.22)",
                      boxShadow: "none",
                    },
                  }}
                >
                  Load package
                </Button> */}
              </Box>

              {/* <Button
                size="small"
                variant="outlined"
                onClick={() => setOpenSolar(true)}
                sx={{
                  ...filterToggleSx,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  whiteSpace: "nowrap",
                  textTransform: "none",
                  ml: 2,
                }}
              >
                <img
                  src="/pickleball_no_logo.png"
                  alt="Pickleball Universe"
                  width={24}
                  height={24}
                  style={{ display: "block", objectFit: "contain" }}
                />
                <Box>See Pickleball Universe</Box>
              </Button> */}
            </Box>

            {/* {viewMode === "package_group_scaffold" ? (
              <PackageSetOverview
                selectedPackageGroup={selectedPackageGroup}
                packageGroupPackages={packageGroupPackages}
              />
            ) : null} */}

            {/* <Box sx={{ display: viewMode === "package_group_scaffold" ? "block" : "none" }}>
  <PackageSetOverview
    selectedPackageGroup={selectedPackageGroup}
    packageGroupPackages={packageGroupPackages}
  />
</Box> */}

            {/* Actual visual tab strip (optional but nice) */}
            <Tabs
              value={activeTab}
              onChange={(_: any, v: any) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons={false}
              sx={{
                display: "none",
                mb: 2,
                minHeight: 30,

                // indicator (underline)
                "& .MuiTabs-indicator": {
                  height: 2,
                  borderRadius: 999,
                  backgroundColor: "rgba(255,255,255,0.55)",
                },

                // tab buttons
                "& .MuiTab-root": {
                  minHeight: 30,
                  padding: "6px 10px",
                  fontSize: 12,
                  letterSpacing: "0.08em",
                  color: "rgba(255,255,255,0.65)",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  opacity: 1, // MUI otherwise dims unselected
                },

                "& .MuiTab-root:hover": {
                  color: "rgba(255,255,255,0.90)",
                },

                "& .MuiTab-root.Mui-selected": {
                  color: "#fff",
                },
              }}
            >
              <Tab value="gameStory" label="Game Story" disableRipple />
              <Tab value="video" label="Video" disableRipple />
              <Tab value="rallyReport" label="Rally Report" disableRipple />
              <Tab
                value="rallyColumnReport"
                label="Rally Column Report"
                disableRipple
              />
              <Tab
                value="finalRallyReport"
                label="Final Rally Report"
                disableRipple
              />
              <Tab
                value="finalRallyAuditReport"
                label="Final Rally Audit"
                disableRipple
              />
            </Tabs>

            {/* TAB PANELS */}

            {activeTab === "gameStory" && selectedId ? (
              <Box
                sx={{
                  display: viewMode === "single_package" ? "block" : "none",
                }}
              >
                <GameStoryPanel
                  packageId={selectedId}
                  onSetTopMatchOutcomeBanner={setGameStoryTopBanner}
                  openQuickTriage={openQuickTriage}
                  onCloseQuickTriage={() => setOpenQuickTriage(false)}
                  onLoadingChange={setIsGameStoryLoading}
                  openSingleTopFocus={openSingleTopFocus}
                  onCloseSingleTopFocus={() => setOpenSingleTopFocus(false)}
                  openThreeMinuteReview={openThreeMinuteReview}
                  onCloseThreeMinuteReview={() =>
                    setOpenThreeMinuteReview(false)
                  }
                  openPlayerLensReview={openPlayerLensReview}
                  onClosePlayerLensReview={() =>
                    setOpenPlayerLensReview(false)
                  }
                  openPositioningPatterns={openPositioningPatterns}
                  onClosePositioningPatterns={() =>
                    setOpenPositioningPatterns(false)
                  }
                  featureLaunchRequest={gameStoryFeatureLaunchRequest}
                />
              </Box>
            ) : null}

            {/* {activeTab === "gameStory" && selectedId ? (
        <GameStoryPanel packageId={selectedId} />
      ) : null}

      {activeTab === "rallyReport" && selectedId ? (
        <RallyReportPanel packageId={selectedId} />
      ) : null}

      {activeTab === "rallyColumnReport" && selectedId ? (
        <RallyColumnReportPanel packageId={selectedId} />
      ) : null}

      {activeTab === "finalRallyReport" && selectedId ? (
        <FinalRallyReportPanel packageId={selectedId} />
      ) : null}

      {activeTab === "finalRallyAuditReport" && selectedId ? (
        <FinalRallyWinnerAuditPanel packageId={selectedId} />
      ) : null} */}

            {/* VIDEO tab = your entire existing “Main layout” */}
            {/* {activeTab === "video" ? (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    mt: 2,
                  }}
                >


                  <Button
                    // component="button"
                    onClick={handleGenerateClips}
                    sx={{
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      border: "none",
                      bgcolor: "#388e3c",
                      color: "#ffffff",
                      cursor: "pointer",
                    }}
                  >
                    Generate clips
                  </Button>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 0 320px",
                        maxWidth: 360,
                        minHeight: 240,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {clips.length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            fontSize: 13,
                          }}
                        >
                          <Box sx={{ fontWeight: 600 }}>
                            Clips ({clips.length}
                            {clipFilter === "final_only" && finalCount
                              ? ` · Finals: ${finalCount}`
                              : ""}
                            )
                          </Box>

                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              //component="button"
                              onClick={() => setClipFilter("all")}
                              sx={{
                                px: 1.25,
                                py: 0.25,
                                borderRadius: 999,
                                border: "none",
                                fontSize: 11,
                                cursor: "pointer",
                                bgcolor:
                                  clipFilter === "all" ? "#1976d2" : "#333",
                                color: "#fff",
                              }}
                            >
                              All
                            </Button>
                            <Button
                              //component="button"
                              onClick={() => setClipFilter("final_only")}
                              sx={{
                                px: 1.25,
                                py: 0.25,
                                borderRadius: 999,
                                border: "none",
                                fontSize: 11,
                                cursor: "pointer",
                                bgcolor:
                                  clipFilter === "final_only"
                                    ? "#1976d2"
                                    : "#333",
                                color: "#fff",
                              }}
                            >
                              Finals {finalCount > 0 ? `(${finalCount})` : ""}
                            </Button>
                          </Box>

                          <Box
                            sx={{
                              maxHeight: 520,
                              overflowY: "auto",
                              borderRadius: 1,
                              border: "1px solid #333",
                              bgcolor: "#111",
                              p: 1,
                            }}
                          >
                            {visibleClips.map((clip) => {
                              const isActive =
                                activeClip?.clipId === clip.clipId;
                              const status: ClipStatus =
                                clipStatusById[clip.clipId] ?? "not_started";

                              const isFinal = !!clip.insight?.is_final;
                              const finalStatus: ClipStatus =
                                finalClipStatusById[clip.clipId] ??
                                "not_started";

                              const flags: string[] = [];
                              if (clip.insight?.is_putaway)
                                flags.push("Putaway");
                              if (clip.insight?.is_reset) flags.push("Reset");
                              if (clip.insight?.is_final) flags.push("Final");

                              const winningTeam = clip.insight?.winning_team as
                                | number
                                | null
                                | undefined;
                              const serverNumber = clip.insight
                                ?.server_number as number | null | undefined;
                              const pointTeam = clip.insight
                                ?.point_awarded_to_team as
                                | number
                                | null
                                | undefined;

                              const metaBits: string[] = [];
                              if (typeof serverNumber === "number") {
                                metaBits.push(`SV: ${serverNumber}`);
                              }
                              if (pointTeam === 0 || pointTeam === 1) {
                                metaBits.push(`Point → T${pointTeam}`);
                              }

                              return (
                                <Box
                                  key={clip.clipId}
                                  onClick={() => handleSelectClip(clip)}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    cursor: "pointer",
                                    mb: 0.5,
                                    bgcolor: isActive ? "#333" : "transparent",
                                    "&:hover": {
                                      bgcolor: isActive ? "#3d3d3d" : "#222",
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      flex: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    <Box sx={{ fontWeight: 500 }}>
                                      {clip.label || "Unknown"} · R
                                      {clip.rallyIndex} S{clip.shotIndex}
                                    </Box>
                                    <Box sx={{ opacity: 0.7, fontSize: 11 }}>
                                      {formatTime(clip.startSec)} –{" "}
                                      {formatTime(clip.endSec)}
                                    </Box>
                                    {metaBits.length > 0 && (
                                      <Box
                                        sx={{
                                          opacity: 0.6,
                                          fontSize: 10,
                                          mt: 0.25,
                                        }}
                                      >
                                        {metaBits.join(" · ")}
                                      </Box>
                                    )}
                                  </Box>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      ml: 1,
                                    }}
                                  >
                                    {flags.length > 0 && (
                                      <Box sx={{ display: "flex", gap: 0.5 }}>
                                        {flags.map((f) => (
                                          <Box
                                            key={f}
                                            sx={{
                                              px: 0.75,
                                              py: 0.25,
                                              borderRadius: 999,
                                              border: "1px solid #555",
                                              fontSize: 10,
                                              textTransform: "uppercase",
                                              letterSpacing: 0.4,
                                            }}
                                          >
                                            {f}
                                          </Box>
                                        ))}
                                      </Box>
                                    )}

                                    {typeof winningTeam === "number" && (
                                      <Box
                                        sx={{
                                          fontSize: 10,
                                          opacity: 0.7,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        WT: {winningTeam}
                                      </Box>
                                    )}

                                    {status === "not_started" && (
                                      <Button
                                        // component="button"
                                        onClick={(e: React.MouseEvent) => {
                                          e.stopPropagation();
                                          void handleAnalyzeClip(clip);
                                        }}
                                        sx={{
                                          px: 1.5,
                                          py: 0.3,
                                          borderRadius: 999,
                                          border: "none",
                                          bgcolor: "#1976d2",
                                          color: "#fff",
                                          fontSize: 11,
                                          cursor: "pointer",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        Do analysis
                                      </Button>
                                    )}

                                    {status === "in_progress" && (
                                      <Box
                                        sx={{
                                          fontSize: 11,
                                          opacity: 0.7,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        Analyzing…
                                      </Box>
                                    )}

                                    {status === "done" && (
                                      <Box
                                        sx={{
                                          fontSize: 11,
                                          color: "#81c784",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        <Box>✓ Analysis ready</Box>

                                        <Button
                                          // component="button"
                                          onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            void handleAnalyzeClip(clip);
                                          }}
                                          sx={{
                                            px: 1.5,
                                            py: 0.3,
                                            borderRadius: 999,
                                            border: "none",
                                            bgcolor: "#1976d2",
                                            color: "#fff",
                                            fontSize: 9,
                                            cursor: "pointer",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          Redo analysis
                                        </Button>
                                      </Box>
                                    )}

                                    {status === "error" && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            fontSize: 11,
                                            color: "#e57373",
                                          }}
                                        >
                                          Error
                                        </Box>
                                        <Button
                                          // component="button"
                                          onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            void handleAnalyzeClip(clip);
                                          }}
                                          sx={{
                                            px: 1,
                                            py: 0.2,
                                            borderRadius: 999,
                                            border: "none",
                                            bgcolor: "#d32f2f",
                                            color: "#fff",
                                            fontSize: 10,
                                            cursor: "pointer",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          Retry
                                        </Button>
                                      </Box>
                                    )}
                                  </Box>

                                  {isFinal && finalStatus === "not_started" && (
                                    <Button
                                      //component="button"
                                      onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        void handleAnalyzeFinalRally(clip);
                                      }}
                                      sx={{
                                        px: 1.5,
                                        py: 0.3,
                                        borderRadius: 999,
                                        border: "none",
                                        bgcolor: "#7b1fa2",
                                        color: "#fff",
                                        fontSize: 11,
                                        cursor: "pointer",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      Do final rally
                                    </Button>
                                  )}

                                  {isFinal && finalStatus === "in_progress" && (
                                    <Box
                                      sx={{
                                        fontSize: 11,
                                        opacity: 0.7,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      Final rally…
                                    </Box>
                                  )}

                                  {isFinal && finalStatus === "done" && (
                                    <Box
                                      sx={{
                                        fontSize: 11,
                                        color: "#ce93d8",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Box>✓ Final ready</Box>
                                      <Button
                                        // component="button"
                                        onClick={(e: React.MouseEvent) => {
                                          e.stopPropagation();
                                          void handleAnalyzeFinalRally(clip);
                                        }}
                                        sx={{
                                          px: 1.5,
                                          py: 0.3,
                                          borderRadius: 999,
                                          border: "none",
                                          bgcolor: "#7b1fa2",
                                          color: "#fff",
                                          fontSize: 9,
                                          cursor: "pointer",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        Redo final
                                      </Button>
                                    </Box>
                                  )}

                                  {isFinal && finalStatus === "error" && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Box
                                        sx={{ fontSize: 11, color: "#e57373" }}
                                      >
                                        Final error
                                      </Box>
                                      <Button
                                        // component="button"
                                        onClick={(e: React.MouseEvent) => {
                                          e.stopPropagation();
                                          void handleAnalyzeFinalRally(clip);
                                        }}
                                        sx={{
                                          px: 1,
                                          py: 0.2,
                                          borderRadius: 999,
                                          border: "none",
                                          bgcolor: "#7b1fa2",
                                          color: "#fff",
                                          fontSize: 10,
                                          cursor: "pointer",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        Retry final
                                      </Button>
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </Box>

                    <Box
                      sx={{
                        flex: "1 1 auto",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          maxWidth: 900,
                        }}
                      >
                        <Box sx={{ minHeight: 110, mb: 1 }}>
                          {activeClip &&
                            activeClipAnalysis &&
                            isWithinActiveClip && (
                              <TechniqueCalloutsOverlay
                                analysis={activeClipAnalysis}
                                clipTimeSec={activeClipTimeSec}
                                playbackRate={playbackRate}
                                strip="top"
                              />
                            )}
                        </Box>

                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            borderRadius: 1,
                            bgcolor: "#000",
                          }}
                        >
                          <video
                            //component="video"
                            ref={videoRef}
                            src={pbvPackage?.videoUrl ?? undefined}
                            onLoadedMetadata={(event: any) => {
                              const dur = event.currentTarget.duration || 0;
                              setDurationSec(dur);
                            }}
                            onTimeUpdate={(event: any) => {
                              const video = event.currentTarget;
                              const t = video.currentTime;
                              setCurrentTimeSec(t);

                              if (
                                isClipLooping &&
                                activeClip &&
                                !video.paused &&
                                t > activeClip.endSec
                              ) {
                                video.currentTime = activeClip.startSec;
                              }
                            }}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            onEnded={() => setIsPlaying(false)}
                            style={{ width: "100%", display: "block" }}
                          />


                        </Box>

                        <Box sx={{ minHeight: 110, mt: 1 }}>
                          {activeClip &&
                            activeClipAnalysis &&
                            isWithinActiveClip && (
                              <TechniqueCalloutsOverlay
                                analysis={activeClipAnalysis}
                                clipTimeSec={activeClipTimeSec}
                                playbackRate={playbackRate}
                                strip="bottom"
                              />
                            )}
                        </Box>

                        <Box
                          sx={{
                            mt: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            fontSize: 14,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: 56,
                                textAlign: "right",
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {formatTime(currentTimeSec)}
                            </Box>

                            <Box sx={{ flex: 1 }}>
                              <input
                                type="range"
                                min={0}
                                max={durationSec || 0}
                                step={0.01}
                                value={currentTimeSec}
                                onChange={handleScrubChange}
                                style={{
                                  width: "100%",
                                  cursor: "pointer",
                                  background: "transparent",
                                }}
                                disabled={durationSec <= 0}
                              />
                            </Box>

                            <Box
                              sx={{
                                minWidth: 56,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {formatTime(durationSec)}
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Button
                              // component="button"
                              onClick={playPause}
                              sx={{
                                px: 2,
                                py: 0.5,
                                borderRadius: 1,
                                border: "none",
                                bgcolor: "#1976d2",
                                color: "#ffffff",
                                cursor: "pointer",
                                fontWeight: 600,
                              }}
                            >
                              {isPlaying ? "Pause" : "Play"}
                            </Button>

                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                //  component="button"
                                onClick={() =>
                                  nudgeVideoSeconds({ deltaSec: -1 })
                                }
                                sx={{
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  border: "none",
                                  bgcolor: "#333",
                                  color: "#fff",
                                  cursor: "pointer",
                                }}
                              >
                                -1s
                              </Button>
                              <Button
                                //  component="button"
                                onClick={() =>
                                  nudgeVideoSeconds({ deltaSec: -0.1 })
                                }
                                sx={{
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  border: "none",
                                  bgcolor: "#333",
                                  color: "#fff",
                                  cursor: "pointer",
                                }}
                              >
                                -0.1s
                              </Button>
                              <Button
                                //    component="button"
                                onClick={() =>
                                  nudgeVideoSeconds({ deltaSec: 0.1 })
                                }
                                sx={{
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  border: "none",
                                  bgcolor: "#333",
                                  color: "#fff",
                                  cursor: "pointer",
                                }}
                              >
                                +0.1s
                              </Button>
                              <Button
                                //component="button"
                                onClick={() =>
                                  nudgeVideoSeconds({ deltaSec: 1 })
                                }
                                sx={{
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  border: "none",
                                  bgcolor: "#333",
                                  color: "#fff",
                                  cursor: "pointer",
                                }}
                              >
                                +1s
                              </Button>
                            </Box>

                            <Box
                              sx={{
                                ml: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              <Box sx={{ fontSize: 12, opacity: 0.8 }}>
                                Speed:
                              </Box>

                              <Button
                                // component="button"
                                onClick={() => handlePlaybackRateChange(1)}
                                sx={{
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 999,
                                  border: "none",
                                  fontSize: 11,
                                  cursor: "pointer",
                                  bgcolor:
                                    playbackRate === 1 ? "#1976d2" : "#333",
                                  color: "#fff",
                                }}
                              >
                                1x
                              </Button>
                              <Button
                                //component="button"
                                onClick={() => handlePlaybackRateChange(0.5)}
                                sx={{
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 999,
                                  border: "none",
                                  fontSize: 11,
                                  cursor: "pointer",
                                  bgcolor:
                                    playbackRate === 0.5 ? "#1976d2" : "#333",
                                  color: "#fff",
                                }}
                              >
                                0.5x
                              </Button>
                              <Button
                                //  component="button"
                                onClick={() => handlePlaybackRateChange(0.25)}
                                sx={{
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 999,
                                  border: "none",
                                  fontSize: 11,
                                  cursor: "pointer",
                                  bgcolor:
                                    playbackRate === 0.25 ? "#1976d2" : "#333",
                                  color: "#fff",
                                }}
                              >
                                0.25x
                              </Button>

                              <Box sx={{ fontSize: 11, opacity: 0.7 }}>
                                ({playbackRate.toFixed(2)}x)
                              </Box>

                              <Box sx={{ fontSize: 14, opacity: 0.6, ml: 1 }}>
                                Microtips show at ≤ 0.5x
                              </Box>
                            </Box>

                            <Box sx={{ ml: "auto", opacity: 0.6 }}>
                              {durationSec > 0
                                ? `Scrubber fraction: ${fraction.toFixed(3)}`
                                : ""}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        flex: "0 0 360px",
                        maxWidth: 420,
                        minHeight: 240,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: "rgba(0,0,0,0.9)",
                          borderRadius: 1.5,
                          p: 1.25,
                          fontSize: "0.75rem",
                          lineHeight: 1.4,
                        }}
                      >
                        <Box
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            mb: 0.5,
                          }}
                        >
                          PB Vision – Current Shot
                        </Box>

                        {shotAny && currentShotDetails.length > 0 ? (
                          <Box component="dl" sx={{ m: 0 }}>
                            {currentShotDetails.map((row) => (
                              <Box
                                key={row.label}
                                sx={{
                                  display: "flex",
                                  mb: 0.25,
                                }}
                              >
                                <Box
                                  component="dt"
                                  sx={{
                                    minWidth: "5.5rem",
                                    fontWeight: 500,
                                    opacity: 0.75,
                                    mr: 0.5,
                                  }}
                                >
                                  {row.label}:
                                </Box>
                                <Box
                                  component="dd"
                                  sx={{
                                    m: 0,
                                    flex: 1,
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {row.value}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{ opacity: 0.6 }}>
                            Move the scrubber into a rally to see PB Vision
                            details.
                          </Box>
                        )}
                      </Box>

                      {activeClip &&
                        activeClipAnalysis &&
                        isWithinActiveClip && (
                          <ShotVerdictPanel
                            analysis={activeClipAnalysis}
                            clipTimeSec={activeClipTimeSec}
                          />
                        )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            ) : null} */}
          </Box>
        </>

        {/* this is the original main page stuff */}

        {/* Main layout */}

        {/* END this is the original main page stuff */}

        {/* previous popups to be deleted eventually, but keeping for now while I build out the new ones */}

        {/* {selectedId ? (
          <FinalRallyReportPopup
            open={finalRallyOpen}
            onClose={() => {
              setFinalRallyOpen(false);
              // optional: also clear selection
              // setSelectedId(null);
            }}
            packageId={selectedId ?? ""}
          />
        ) : null} */}

        {/* {selectedId ? (
          <FinalRallyWinnerAuditPopup
            open={finalRallyWinnerAuditOpen}
            onClose={() => {
              setRallyWinnerAuditOpen(false);
              // optional: also clear selection
              // setSelectedId(null);
            }}
            packageId={selectedId ?? ""}
          />
        ) : null} */}

        {/* {selectedId ? (
        <GameStoryDialog
          open={gameStoryOpen}
          onClose={() => setGameStoryOpen(false)}
          packageId={selectedId ?? ""}
        />
      ) : null} */}

        {selectedId ? (
          <RallyPopup
            open={kitchenArrivalOpen}
            onClose={() => {
              setKitchenArrival(false);
              // optional: also clear selection
              // setSelectedId(null);
            }}
            packageId={selectedId ?? ""}
          />
        ) : null}

        {/* {selectedId ? (
          <RallyBoardPopup
          rallyBadgesRef={null}
            open={rallyColumnOpen}
            onClose={() => {
              setRallyColumnOpen(false);
              // optional: also clear selection
              // setSelectedId(null);
            }}
            packageId={selectedId ?? ""}
          />
        ) : null} */}
      </Box>

      <ExplorerNotesPopup
        open={openExplorerNotes}
        onClose={() => setOpenExplorerNotes(false)}
      />

      {openFeatureList ? (
        <Paper
          className="pbv-feature-list-dialog"
          data-demo="feature-list-dialog"
          elevation={20}
          sx={{
            position: "fixed",
            left: featureListPosition.x,
            top: featureListPosition.y,
            zIndex: 1250,
            width: featureListSize.width,
            height: featureListSize.height,
            display: "flex",
            flexDirection: "column",
            bgcolor: "#111",
            color: "rgba(255,255,255,0.92)",
            border: "1px solid rgba(255,255,255,0.16)",
            borderRadius: 1,
            overflow: "hidden",
            boxShadow: "0 24px 80px rgba(0,0,0,0.62)",
            "body[data-pbv-demo-capture-layout='true'] &": {
              left: "28px",
              top: "32px",
            },
            "body[data-pbv-demo-recording='true'] & button:focus, body[data-pbv-demo-recording='true'] & button:focus-visible, body[data-pbv-demo-recording='true'] & .Mui-focusVisible": {
              outline: "none",
            },
          }}
        >
        <DialogTitle
          onPointerDown={beginFeatureListDrag}
          onPointerMove={moveFeatureListDrag}
          onPointerUp={endFeatureListDrag}
          onPointerCancel={endFeatureListDrag}
          sx={{
            cursor: "move",
            fontWeight: 900,
            py: 1,
            pr: 6,
            position: "relative",
            userSelect: "none",
            "body[data-pbv-demo-recording='true'] &": {
              fontSize: 20,
            },
          }}
        >
          Pickleball Game Explorer Features
          <IconButton
            data-demo="feature-list-close-top"
            aria-label="Close feature list"
            size="small"
            onClick={() => setOpenFeatureList(false)}
            sx={{
              position: "absolute",
              right: 10,
              top: 10,
              color: "rgba(255,255,255,0.78)",
              border: "1px solid rgba(255,255,255,0.16)",
              bgcolor: "rgba(255,255,255,0.04)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            borderColor: "rgba(255,255,255,0.12)",
            overflow: "auto",
            "body[data-pbv-demo-recording='true'] &": {
              px: 2,
              py: 1.25,
            },
          }}
        >
          <Typography
            sx={{
              mb: 1,
              fontSize: 12,
              color: "rgba(255,255,255,0.64)",
              "body[data-pbv-demo-recording='true'] &": {
                display: "none",
              },
            }}
          >
            Maintained feature inventory. Update{" "}
            <Box component="span" sx={{ fontFamily: "monospace" }}>
              GameStory_features.ts
            </Box>{" "}
            when a feature is added, deleted, renamed, or materially changed.
            Last updated {GAME_STORY_FEATURES_UPDATED_AT}.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 1,
              alignItems: "start",
            }}
          >
            {[0, 1, 2].map((columnIndex) => (
              <Box
                key={`feature-column-${columnIndex}`}
                sx={{ display: "flex", flexDirection: "column", gap: 1 }}
              >
                {featureGroups
                  .filter((_, groupIndex) => groupIndex % 3 === columnIndex)
                  .map(([area, features]) => (
              <Box key={area}>
                <Typography
                  sx={{
                    mb: 0.4,
                    fontSize: 11,
                    fontWeight: 900,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#66b3ff",
                    "body[data-pbv-demo-recording='true'] &": {
                      fontSize: 10,
                    },
                  }}
                >
                  {area}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.55,
                    "body[data-pbv-demo-recording='true'] &": {
                      gap: 0.45,
                    },
                  }}
                >
                  {features.map((feature) => (
                    <Box
                      component={feature.launchKey ? "button" : "div"}
                      key={`${feature.area}_${feature.title}`}
                      data-feature-key={feature.launchKey ?? undefined}
                      onClick={() => launchFeatureFromList(feature.launchKey)}
                      sx={{
                        width: "100%",
                        textAlign: "left",
                        font: "inherit",
                        color: "inherit",
                        borderRadius: 1,
                        border: "1px solid rgba(255,255,255,0.11)",
                        bgcolor: "rgba(255,255,255,0.045)",
                        px: 0.9,
                        py: 0.7,
                        cursor: feature.launchKey ? "pointer" : "default",
                        display: "block",
                        appearance: "none",
                        "&:hover": feature.launchKey
                          ? {
                              borderColor: "rgba(102,179,255,0.5)",
                              bgcolor: "rgba(102,179,255,0.10)",
                            }
                          : undefined,
                        "&:focus-visible": feature.launchKey
                          ? {
                              outline: "2px solid rgba(102,179,255,0.8)",
                              outlineOffset: 2,
                            }
                          : undefined,
                        "body[data-pbv-demo-recording='true'] &:focus, body[data-pbv-demo-recording='true'] &:focus-visible, body[data-pbv-demo-recording='true'] &.Mui-focusVisible": feature.launchKey
                          ? {
                              outline: "none",
                            }
                          : undefined,
                        "body[data-pbv-demo-recording='true'] &": {
                          px: 0.8,
                          py: 0.55,
                        },
                        "body[data-pbv-demo-feature-focus~='paddle-readiness'] &[data-feature-key='paddle-readiness'], body[data-pbv-demo-feature-focus~='left-side-coverage'] &[data-feature-key='left-side-coverage'], body[data-pbv-demo-feature-focus~='candidate-speedups'] &[data-feature-key='candidate-speedups'], body[data-pbv-demo-feature-focus~='candidate-bodybags'] &[data-feature-key='candidate-bodybags'], body[data-pbv-demo-feature-focus~='serve-return-risk'] &[data-feature-key='serve-return-risk'], body[data-pbv-demo-feature-focus~='opportunity-finder'] &[data-feature-key='opportunity-finder'], body[data-pbv-demo-feature-focus~='next-ball-reads'] &[data-feature-key='next-ball-reads']": {
                          borderColor: "rgba(255,213,79,0.96)",
                          bgcolor: "rgba(255,213,79,0.16)",
                          boxShadow:
                            "0 0 0 2px rgba(255,213,79,0.42), 0 0 28px rgba(255,213,79,0.24)",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.7,
                          fontWeight: 900,
                          fontSize: 14,
                          "body[data-pbv-demo-recording='true'] &": {
                            fontSize: 11.5,
                            lineHeight: 1.15,
                          },
                        }}
                      >
                        {feature.launchKey ? (
                          <Box
                            component="span"
                            sx={{
                              width: feature.launchKey === "pounce" ? 46 : 22,
                              minWidth: feature.launchKey === "pounce" ? 46 : 22,
                              height: 22,
                              borderRadius: 0.75,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "rgba(255,255,255,0.92)",
                              bgcolor: "rgba(102,179,255,0.12)",
                              border: "1px solid rgba(102,179,255,0.24)",
                              "body[data-pbv-demo-recording='true'] &": {
                                width: feature.launchKey === "pounce" ? 46 : 18,
                                minWidth: feature.launchKey === "pounce" ? 46 : 18,
                                height: 18,
                              },
                            }}
                          >
                            {featureLaunchIcon(feature.launchKey)}
                          </Box>
                        ) : null}
                        <Box component="span" sx={{ flex: "1 1 auto" }}>
                          {feature.title}
                        </Box>
                        {feature.launchKey ? (
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.35,
                              color: "#66b3ff",
                              fontSize: 11,
                              fontWeight: 900,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              "body[data-pbv-demo-recording='true'] &": {
                                display: "none",
                              },
                            }}
                          >
                            Open <OpenInNewIcon sx={{ fontSize: 13 }} />
                          </Box>
                        ) : null}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.35,
                          fontSize: 13,
                          color: "rgba(255,255,255,0.78)",
                          "body[data-pbv-demo-recording='true'] &": {
                            mt: 0.2,
                            fontSize: 10.5,
                            lineHeight: 1.22,
                          },
                        }}
                      >
                        <Box component="span" sx={{ fontWeight: 800 }}>
                          Get there:
                        </Box>{" "}
                        {feature.howToOpen}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.25,
                          fontSize: 13,
                          color: "rgba(255,255,255,0.70)",
                          "body[data-pbv-demo-recording='true'] &": {
                            display: "none",
                          },
                        }}
                      >
                        <Box component="span" sx={{ fontWeight: 800 }}>
                          Relevant when:
                        </Box>{" "}
                        {feature.relevantWhen}
                      </Typography>
                      {feature.source ? (
                        <Typography
                          sx={{
                            mt: 0.35,
                            fontSize: 11,
                            color: "rgba(255,255,255,0.42)",
                            fontFamily: "monospace",
                            "body[data-pbv-demo-recording='true'] &": {
                              display: "none",
                            },
                          }}
                        >
                          {feature.source}
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </Box>
                  ))}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, justifyContent: "space-between" }}>
          <Button
            data-demo="feature-list-close"
            onClick={() => setOpenFeatureList(false)}
            sx={{
              color: "rgba(255,255,255,0.78)",
              textTransform: "none",
              "body[data-pbv-demo-recording='true'] &:focus, body[data-pbv-demo-recording='true'] &:focus-visible, body[data-pbv-demo-recording='true'] &.Mui-focusVisible": {
                outline: "none",
              },
            }}
          >
            Close
          </Button>
          <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.42)" }}>
            Drag the title bar to move. Drag the corner to resize.
          </Typography>
        </DialogActions>
        <Box
          aria-label="Resize feature list"
          onPointerDown={beginFeatureListResize}
          onPointerMove={moveFeatureListResize}
          onPointerUp={endFeatureListResize}
          onPointerCancel={endFeatureListResize}
          sx={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: 24,
            height: 24,
            cursor: "nwse-resize",
            touchAction: "none",
            background:
              "linear-gradient(135deg, transparent 0 45%, rgba(255,255,255,0.24) 46% 53%, transparent 54% 64%, rgba(255,255,255,0.42) 65% 72%, transparent 73%)",
          }}
        />
      </Paper>
      ) : null}

      <PbSkillsSolarPopup
        open={openSolar}
        onClose={() => setOpenSolar(false)}
      />

      <DemoRunOverlay />

      {/* <Dialog
        open={openWhatIsThis}
        onClose={() => setOpenWhatIsThis(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#1e1e1e",
            color: "#f3f3f3",
            border: "1px solid rgba(255,255,255,0.16)",
            boxShadow: "0 18px 60px rgba(0,0,0,0.42)",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: "0.01em",
            color: "#ffffff",
          }}
        >
          What is this?
        </DialogTitle>

        <DialogContent
          sx={{
            pt: "6px !important",
            fontSize: 18,
            lineHeight: 1.65,
            color: "rgba(255,255,255,0.96)",
          }}
        >

          <Box sx={{ mb: 2 }}>
            <Box sx={{ mb: 1.5 }}>
              <Box component="span" sx={{ fontWeight: 600, color: "#ffffff" }}>
                Pickleball Game Explorer
              </Box>{" "}
              is a way to explore a single pickleball game in detail.
            </Box>

            <Box sx={{ mb: 1.5 }}>
              Much of what shapes a pickleball game happens inside short
              sequences that can vanish from memory once the game ends.
            </Box>

            <Box sx={{ mb: 1.5 }}>
              Pressure builds. Opportunities briefly appear. Small choices
              change outcomes.
            </Box>

            <Box sx={{ mb: 1.5 }}>
              A single pickleball game can contain more buried structure than it first appears.
            </Box>
            <Box sx={{ mb: 1.5 }}>
                            This tool helps make that structure visible and easier to explore.
            </Box>

            <Box sx={{ mb: 1.5 }}>
              You can move through rallies quickly, revisit short sequences,
              compare shapes in serve/return and 3rd/4th-shot play, and explore
              moments where attack chances appeared or disappeared.
            </Box>

            <Box sx={{ mb: 1.5 }}>
              AI is available throughout the interface if you want to ask questions
              about a specific rally clip, a short sequence within a rally, or an entire game.
            </Box>

            <Box sx={{ mb: 0 }}>

              This tool makes it possible to return to and explore the parts of a game that
usually pass too quickly during play to notice clearly.
          
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 0.5 }}>
          <Button
            onClick={() => setOpenWhatIsThis(false)}
            sx={{
              ...filterToggleSx,
              textTransform: "none",
              px: 1.4,
              borderColor: "rgba(255,255,255,0.18)",
              backgroundColor: "rgba(255,255,255,0.04)",
              color: "rgba(255,255,255,0.96)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.08)",
                borderColor: "rgba(255,255,255,0.24)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog> */}

      <Dialog
        open={openWhatIsThis}
        onClose={() => setOpenWhatIsThis(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#1e1e1e",
            color: "#f3f3f3",
            border: "1px solid rgba(255,255,255,0.16)",
            boxShadow: "0 18px 60px rgba(0,0,0,0.42)",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: "0.01em",
            color: "#ffffff",
          }}
        >
          What is this?
        </DialogTitle>

        <DialogContent
          sx={{
            pt: "6px !important",
            fontSize: 18,
            lineHeight: 1.65,
            color: "#e6ecf5",
          }}
        >
          <Box sx={{ mb: 2, color: "#e6ecf5" }}>
            <Box sx={{ mb: 1.5 }}>
              <Box component="span" sx={{ fontWeight: 600, color: "#ffffff" }}>
                Pickleball Game Explorer
              </Box>{" "}
              is a way to explore a single pickleball game in detail.
            </Box>

            <Box sx={{ mb: 1.5 }}>
              Much of what shapes a pickleball game happens inside short
              sequences that can vanish from memory once the game ends.
            </Box>

            <Box sx={{ mb: 1.5, color: "#f2f6ff" }}>
              Pressure builds. Opportunities briefly appear. Small choices
              change outcomes.
            </Box>

            <Box sx={{ mb: 1.5 }}>
              A single pickleball game can contain more buried structure than it
              first appears.
            </Box>

            <Box sx={{ mb: 1.5 }}>
              This tool helps make that structure visible and easier to explore.
            </Box>

            <Box sx={{ mb: 1.5 }}>
              You can move through rallies quickly, revisit short sequences,
              compare shapes in serve/return and 3rd/4th-shot play, and explore
              moments where attack chances appeared or disappeared.
            </Box>

            <Box sx={{ mb: 1.5 }}>
              AI is available throughout the interface if you want to ask
              questions about a specific rally clip, a short sequence within a
              rally, or an entire game.
            </Box>

            <Box sx={{ mb: 1.5 }}>
              This tool is currently built using PB Vision match data, but the
              underlying approach is designed to work with similar rally-level
              tracking data from other tracking systems.
            </Box>

            <Box sx={{ mb: 0 }}>
              This tool makes it possible to return to and explore the parts of
              a game that usually pass too quickly during play to remember.
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 0.5 }}>
          <Button
            onClick={() => setOpenWhatIsThis(false)}
            sx={{
              ...filterToggleSx,
              textTransform: "none",
              px: 1.4,
              borderColor: "rgba(255,255,255,0.18)",
              backgroundColor: "rgba(255,255,255,0.04)",
              color: "rgba(255,255,255,0.96)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.08)",
                borderColor: "rgba(255,255,255,0.24)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddGameNote}
        onClose={() => setOpenAddGameNote(false)}
        maxWidth="sm"
        fullWidth
        data-demo="add-your-game-dialog"
        data-happy-path="add-your-game-dialog"
        PaperProps={{
          sx: {
            bgcolor: "#1e1e1e",
            color: "#f3f3f3",
            border: "1px solid rgba(255,255,255,0.16)",
            boxShadow: "0 18px 60px rgba(0,0,0,0.42)",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: "0.01em",
            color: "#ffffff",
          }}
        >
          Want to see your game here?
        </DialogTitle>

        <DialogContent
          sx={{
            pt: "6px !important",
            fontSize: 17,
            lineHeight: 1.6,
            color: "#e6ecf5",
          }}
        >
          <Box sx={{ mb: 1.5 }}>
            {PRODUCT_NAME_WITH_STAGE_PARENS} is beta software. There is no self-serve
            upload or login yet, so adding a game is currently manual.
          </Box>

          <Box sx={{ mb: 1.5 }}>
            No personal information like player names will be shown. I will use
            a generic anonymous game name in the app.
          </Box>

          <Box sx={{ mb: 1 }}>
            If you want one of your games included for testing, email{" "}
            <Box
              component="a"
              href={`mailto:${CONTACT_EMAIL}?subject=Pickleball%20Game%20Explorer%20-%20Add%20my%20game`}
              sx={{ color: "#f7d76b", fontWeight: 700 }}
            >
              {CONTACT_EMAIL}
            </Box>{" "}
            to reach {CREATOR_NAME} with:
          </Box>

          <Box component="ul" sx={{ mt: 0, mb: 1.5, pl: 3 }}>
            <Box component="li">a link to your game in PB Vision</Box>
            <Box component="li">
              or a link to your game video so it can be added as one of the
              sample games
            </Box>
            <Box component="li">
              optional: the PB Vision insights.json file and matching cv.json
              file, if you already have the exports
            </Box>
          </Box>

            <Box
              component="a"
              href="https://help.pb.vision/articles/2010377-advanced-insights-with-data-exports"
              sx={{ color: "#f7d76b", fontWeight: 700 }}
              target="_blank"
            >
              This page
            </Box>
            {" "}describes how to get the two json files from PB Vision.
            
          <Box sx={{ mb: 0, marginTop: "6px", }}>
            A PB Vision game link may be enough if it gives access to the data
            and video. If not, I may ask for the files or video link directly.
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 0.5 }}>
          <Button
            href={`mailto:${CONTACT_EMAIL}?subject=Pickleball%20Game%20Explorer%20-%20Add%20my%20game`}
            sx={{
              textTransform: "none",
              px: 1.4,
              borderRadius: 1,
              fontWeight: 700,
              color: "#111111",
              bgcolor: "#f7d76b",
              "&:hover": {
                bgcolor: "#ffe38a",
              },
            }}
          >
            Email Brad
          </Button>
          <Button
            onClick={() => setOpenAddGameNote(false)}
            data-demo="add-your-game-close"
            data-happy-path="add-your-game-close"
            sx={{
              ...filterToggleSx,
              textTransform: "none",
              px: 1.4,
              borderColor: "rgba(255,255,255,0.18)",
              backgroundColor: "rgba(255,255,255,0.04)",
              color: "rgba(255,255,255,0.96)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.08)",
                borderColor: "rgba(255,255,255,0.24)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* ---------- Old on-video verdict overlay (kept for reference) ---------- */

/* ---------- Technique overlay + side verdict panel ---------- */

export type TechniqueNote = {
  timeSecClip: number;
  bodyFocus: string;
  noteType: "strength" | "refinement";
  description: string;
  shortCue: string;
  focusXNorm?: number | null;
  focusYNorm?: number | null;
};

type TechniqueCalloutsOverlayProps = {
  analysis: ClipAnalysis;
  clipTimeSec: number;
  playbackRate: number;
  strip: "top" | "bottom";
};

type ShotVerdictPanelProps = {
  analysis: ClipAnalysis;
  clipTimeSec: number;
};

export function ShotVerdictPanel({
  analysis,
  clipTimeSec,
}: ShotVerdictPanelProps) {
  const players = analysis.players ?? [];
  const striker = players.find((p) => p.isStrikerForPrimaryShot) ?? players[0];

  let currentMoment: NoteworthyMoment | null = null;

  if (striker?.noteworthyMoments && striker.noteworthyMoments.length > 0) {
    const moments = [...striker.noteworthyMoments].sort(
      (a, b) => (a.timeSecClip ?? 0) - (b.timeSecClip ?? 0),
    );

    currentMoment = moments[0];
    for (const m of moments) {
      if ((m.timeSecClip ?? 0) <= clipTimeSec + 1e-3) {
        currentMoment = m;
      } else break;
    }
  }

  const clipVerdict = analysis.rallyContext?.verdict || "";
  const primarySummary = analysis.primaryShot?.coachSummary || "";
  const strikerVerdict = striker?.verdictForPlayer || "";
  const strikerDrill = striker?.refinementOrDrillFocusForPlayer || "";
  const momentVerdict =
    currentMoment?.verdictForMoment || currentMoment?.description || "";

  if (
    !momentVerdict &&
    !primarySummary &&
    !clipVerdict &&
    !strikerVerdict &&
    !strikerDrill
  ) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 1,
        bgcolor: "rgba(0,0,0,0.8)",
        borderRadius: 1.5,
        p: 1.5,
        fontSize: 14,
        color: "white",
      }}
    >
      {momentVerdict && (
        <Box sx={{ mb: 0.75, lineHeight: 1.4 }}>
          <Box sx={{ fontSize: 11, opacity: 0.8 }}>
            {currentMoment?.timeSecClip != null &&
              `${currentMoment.timeSecClip.toFixed(2)}s · ${
                currentMoment.momentType
              }`}
          </Box>
          <Box sx={{ fontWeight: 700, mt: 0.25 }}>{momentVerdict}</Box>
        </Box>
      )}

      {strikerVerdict && (
        <Box
          sx={{
            mb: 0.75,
            pt: 0.5,
            borderTop: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <strong>Striker:</strong> {strikerVerdict}
        </Box>
      )}

      {(primarySummary || clipVerdict) && (
        <Box
          sx={{
            mb: 0.75,
            pt: 0.5,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            lineHeight: 1.4,
            opacity: 0.9,
          }}
        >
          {primarySummary && <Box sx={{ mb: 0.25 }}>{primarySummary}</Box>}
          {clipVerdict && (
            <Box sx={{ fontSize: 11, opacity: 0.85 }}>{clipVerdict}</Box>
          )}
        </Box>
      )}

      {strikerDrill && (
        <Box
          sx={{
            mt: 0.5,
            pt: 0.5,
            borderTop: "1px solid rgba(255,255,255,0.14)",
            fontStyle: "italic",
            opacity: 0.95,
          }}
        >
          <strong>Drill focus:</strong> {strikerDrill}
        </Box>
      )}
    </Box>
  );
}

type PBVShotInfoPanelProps = {
  shot: PBVShot | null;
  currentMs: number | null;
  insights?: PBVInsights | null;
};

function toLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

function PBVShotInfoPanel({
  shot,
  currentMs,
  insights,
}: PBVShotInfoPanelProps) {
  if (!shot) {
    return (
      <Box
        sx={{
          mb: 1.5,
          bgcolor: "rgba(0,0,0,0.7)",
          borderRadius: 1.5,
          p: 1,
          fontSize: 11,
          color: "#ddd",
          minHeight: 90,
        }}
      >
        <Box sx={{ fontWeight: 600, opacity: 0.8 }}>
          PB Vision – Current Shot
        </Box>
        <Box sx={{ opacity: 0.6, mt: 0.5 }}>Waiting for shot info…</Box>
      </Box>
    );
  }

  const anyShot = shot as any;

  const startMs: number | null =
    typeof anyShot.start_ms === "number" ? anyShot.start_ms : null;
  const endMs: number | null =
    typeof anyShot.end_ms === "number" ? anyShot.end_ms : null;

  const durationMs = startMs != null && endMs != null ? endMs - startMs : null;
  const offsetMs =
    startMs != null && currentMs != null ? currentMs - startMs : null;

  const playerId: number | undefined =
    typeof anyShot.player_id === "number" ? anyShot.player_id : undefined;
  const shotType: string | undefined =
    typeof anyShot.shot_type === "string" ? anyShot.shot_type : undefined;

  const speedRaw =
    typeof anyShot.speed === "number"
      ? anyShot.speed
      : typeof anyShot.speed_mph === "number"
        ? anyShot.speed_mph
        : typeof anyShot.speed_kph === "number"
          ? anyShot.speed_kph
          : undefined;

  const flags: string[] = [];
  if (shot.is_putaway) flags.push("Putaway");
  if (shot.is_reset) flags.push("Reset");
  if (shot.is_final) flags.push("Final ball");

  const winningTeam: number | null | undefined = anyShot.winning_team;
  const servingTeam: number | null | undefined = anyShot.serving_team;
  const pointTeam: number | null | undefined = anyShot.point_awarded_to_team;

  const timeLine =
    currentMs != null && startMs != null && endMs != null && durationMs != null
      ? `Time: ${currentMs} ms (shot ${startMs}–${endMs} ms, +${
          offsetMs ?? 0
        } ms / ${durationMs} ms)`
      : currentMs != null
        ? `Time: ${currentMs} ms`
        : "";

  let playerLine: string | undefined;
  if (insights && typeof playerId === "number") {
    const anyInsights = insights as any;
    const playersArr =
      anyInsights.session?.players ??
      anyInsights.players ??
      anyInsights.session?.player_info;

    let meta: any | undefined;
    if (Array.isArray(playersArr)) {
      meta = playersArr.find(
        (p: any) =>
          p.id === playerId ||
          p.player_id === playerId ||
          p.pbv_player_id === playerId,
      );
    }

    const name = meta?.name ?? meta?.label ?? meta?.display_name ?? undefined;
    const handedness = meta?.handedness ?? meta?.hand ?? undefined;

    const bits: string[] = [];
    bits.push(`P${playerId}`);
    if (name) bits.push(name);
    if (handedness) bits.push(handedness);
    playerLine = `Player: ${bits.join(" • ")}`;
  } else if (typeof playerId === "number") {
    playerLine = `Player: P${playerId}`;
  }

  const tagsObj = (shot as any).tags;
  const tagEntries =
    tagsObj && typeof tagsObj === "object"
      ? Object.entries(tagsObj).filter(
          ([, v]) =>
            v !== null &&
            v !== undefined &&
            typeof v !== "object" &&
            `${v}`.trim() !== "",
        )
      : [];

  const primaryTags = tagEntries.slice(0, 4);

  return (
    <Box
      sx={{
        mb: 1.5,
        bgcolor: "rgba(0,0,0,0.85)",
        borderRadius: 1.5,
        p: 1.25,
        fontSize: 11,
        color: "#f5f5f5",
        boxShadow: 3,
        minHeight: 100,
      }}
    >
      <Box sx={{ fontWeight: 600, mb: 0.5 }}>PB Vision – Current Shot</Box>

      {timeLine && <Box sx={{ lineHeight: 1.3 }}>{timeLine}</Box>}

      {playerLine && <Box sx={{ lineHeight: 1.3 }}>{playerLine}</Box>}

      {shotType && <Box sx={{ lineHeight: 1.3 }}>Shot type: {shotType}</Box>}

      {typeof speedRaw === "number" && (
        <Box sx={{ lineHeight: 1.3 }}>
          Speed: {speedRaw.toFixed(1)} (PBV units)
        </Box>
      )}

      {(servingTeam != null || winningTeam != null || pointTeam != null) && (
        <Box sx={{ mt: 0.4, lineHeight: 1.3, opacity: 0.9 }}>
          {servingTeam != null && (
            <span>SV team: T{servingTeam}&nbsp;&nbsp;</span>
          )}
          {winningTeam != null && <span>WT: T{winningTeam}&nbsp;&nbsp;</span>}
          {pointTeam != null && <span>Point → T{pointTeam}</span>}
        </Box>
      )}

      {flags.length > 0 && (
        <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {flags.map((f) => (
            <Box
              key={f}
              sx={{
                px: 0.75,
                py: 0.1,
                borderRadius: 999,
                border: "1px solid rgba(255,255,255,0.25)",
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            >
              {f}
            </Box>
          ))}
        </Box>
      )}

      {primaryTags.length > 0 && (
        <Box
          sx={{
            mt: 0.7,
            pt: 0.4,
            borderTop: "1px solid rgba(255,255,255,0.12)",
            lineHeight: 1.3,
          }}
        >
          {primaryTags.map(([k, v]) => (
            <Box
              key={k}
              sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
            >
              <Box sx={{ opacity: 0.65 }}>{toLabel(k)}:</Box>
              <Box sx={{ fontWeight: 500 }}>{String(v)}</Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

const MICRO_SLOW_THRESHOLD = 0.5;

// Rough anchors for each PBV player id (0–3).
//  - 0,1 = near side (bottom of screen)
//  - 2,3 = far side (top of screen)
// (Kept here for future on-video positioning if we want it again.)
const POSITION_BY_PLAYER_ID: Record<number, { xNorm: number; yNorm: number }> =
  {
    0: { xNorm: 0.18, yNorm: 0.75 }, // near-left
    1: { xNorm: 0.82, yNorm: 0.75 }, // near-right
    2: { xNorm: 0.18, yNorm: 0.25 }, // far-left
    3: { xNorm: 0.82, yNorm: 0.25 }, // far-right
  };

// Normalize PBV ids in case the model ever sends 0–3 instead of 1–4.
const normalizeId = (rawId: number | undefined): number => {
  if (rawId == null || Number.isNaN(rawId)) return 1;
  // If PBV used 0–3, shift to 1–4.
  if (rawId >= 0 && rawId <= 3) return rawId + 1;
  return rawId;
};

// TechniqueCalloutsOverlay.tsx
// import React, { memo, useMemo } from "react";
// import { Box, Typography } from "@mui/material";

// type TechniqueCalloutsOverlayProps = {
//   analysis: any | null | undefined; // expects analysis.players[]
//   clipTimeSec?: number;
//   playbackRate?: number;
//   strip?: "top" | "bottom";
// };

export const TechniqueCalloutsOverlay = memo(
  ({
    analysis,
    clipTimeSec,
    playbackRate: _playbackRate,
    strip,
  }: TechniqueCalloutsOverlayProps) => {
    const playersRaw: any[] | undefined = analysis?.players;

    // Dedup by pbvPlayerId
    const uniquePlayers = React.useMemo(() => {
      if (!playersRaw || playersRaw.length === 0) return [];

      const byId: Record<number, any> = {};
      for (const p of playersRaw) {
        if (!p || typeof p.pbvPlayerId !== "number") continue;
        if (!byId[p.pbvPlayerId]) {
          byId[p.pbvPlayerId] = p;
        }
      }
      return Object.values(byId);
    }, [playersRaw]);

    // Filter based on strip:
    //  - "top"    → P3, P4
    //  - "bottom" → P1, P2
    const filteredPlayers = React.useMemo(() => {
      if (!uniquePlayers.length) return [];
      if (!strip) return uniquePlayers;

      const topIds = new Set([3, 4]);
      const bottomIds = new Set([1, 2]);

      return uniquePlayers.filter((p: any) => {
        const id = p.pbvPlayerId;
        if (typeof id !== "number") return false;
        if (strip === "top") return topIds.has(id);
        return bottomIds.has(id); // "bottom"
      });
    }, [uniquePlayers, strip]);

    const now = clipTimeSec ?? 0;
    const ACTIVE_WINDOW_SEC = 0.6;

    // Time-based note lookup per filtered player
    const perPlayerNoteInfo = React.useMemo(() => {
      const info: Record<
        number,
        { activeNote: any | null; isActive: boolean }
      > = {};

      for (const player of filteredPlayers) {
        const id: number = player.pbvPlayerId;
        const notes: any[] = Array.isArray(player.techniqueNotes)
          ? player.techniqueNotes
          : [];

        let bestNote: any | null = null;
        let bestDist = Infinity;

        for (const note of notes) {
          const t =
            typeof note?.timeSecClip === "number" ? note.timeSecClip : null;
          if (t == null) continue;
          const dist = Math.abs(t - now);
          if (dist < bestDist) {
            bestDist = dist;
            bestNote = note;
          }
        }

        const isActive =
          bestNote != null && bestDist <= ACTIVE_WINDOW_SEC ? true : false;

        info[id] = { activeNote: bestNote, isActive };
      }

      return info;
    }, [filteredPlayers, now]);

    // ✅ Hooks all called before any return
    if (filteredPlayers.length === 0) return null;

    const renderCardContent = (player: any) => {
      const { activeNote, isActive } = perPlayerNoteInfo[
        player.pbvPlayerId
      ] || {
        activeNote: null,
        isActive: false,
      };

      // const appearanceLabel =
      //   player?.appearance?.shortLabel ?? `P${player.pbvPlayerId}`;

      const appearanceLabel = player?.appearance?.shortLabel ?? "";

      const idLabel =
        typeof player?.pbvPlayerId === "number"
          ? `P${player.pbvPlayerId}`
          : "Player";

      const headerLabel = appearanceLabel
        ? `${idLabel} – ${appearanceLabel}`
        : idLabel;

      const cue = activeNote?.shortCue ?? "";

      const description =
        activeNote?.description ||
        player?.verdictForPlayer ||
        player?.refinementOrDrillFocusForPlayer ||
        "";

      return (
        <Box
          sx={{
            borderRadius: 1,
            px: 1.5,
            py: 1,
            bgcolor: isActive ? "rgba(0,0,0,0.9)" : "rgba(0,0,0,0.6)",
            color: "#fff",
            maxWidth: 380,
            boxShadow: 3,
            border: "1px solid rgba(255,255,255,0.25)",
            fontSize: 12,
            transform: isActive ? "scale(1.02)" : "scale(0.98)",
            transition:
              "background-color 120ms ease, transform 120ms ease, opacity 120ms ease",
            opacity: description || cue ? 1 : 0.5,
          }}
        >
          <Box
            sx={{
              fontSize: 11,
              fontWeight: 600,
              opacity: 0.85,
              mb: 0.25,
              lineHeight: 1.2,
            }}
          >
            {`${headerLabel}`}
          </Box>

          {cue && (
            <Box
              sx={{
                fontSize: 11,
                fontWeight: 600,
                mb: 0.25,
                lineHeight: 1.3,
              }}
            >
              {cue}
            </Box>
          )}

          {description && (
            <Box
              sx={{
                fontSize: 11,
                lineHeight: 1.3,
                opacity: 0.9,
              }}
            >
              {description}
            </Box>
          )}
        </Box>
      );
    };

    // Lay out cards inside the reserved strip — no absolute positioning,
    // so they can’t intersect the video element.
    // Top strip: align to top; bottom strip: align to bottom.
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: strip === "top" ? "flex-start" : "flex-end",
          gap: 2,
        }}
      >
        {filteredPlayers
          .slice() // sort so “left” is lower id
          .sort((a: any, b: any) => (a.pbvPlayerId ?? 0) - (b.pbvPlayerId ?? 0))
          .map((player: any) => (
            <Box key={player.pbvPlayerId}>{renderCardContent(player)}</Box>
          ))}
      </Box>
    );
  },
);

export const TechniqueCalloutsOverlay_oldold = memo(
  ({
    analysis,
    clipTimeSec,
    playbackRate: _playbackRate,
    strip,
  }: TechniqueCalloutsOverlayProps) => {
    const playersRaw: any[] | undefined = analysis?.players;

    // 1) Deduplicate players by pbvPlayerId
    const uniquePlayers = React.useMemo(() => {
      if (!playersRaw || playersRaw.length === 0) return [];

      const byId: Record<number, any> = {};
      for (const p of playersRaw) {
        if (!p || typeof p.pbvPlayerId !== "number") continue;
        if (!byId[p.pbvPlayerId]) {
          byId[p.pbvPlayerId] = p;
        }
      }
      return Object.values(byId);
    }, [playersRaw]);

    // 2) Filter players based on strip:
    //    - "top"    → P3, P4 (far side)
    //    - "bottom" → P1, P2 (near side)
    const filteredPlayers = React.useMemo(() => {
      if (!uniquePlayers.length) return [];

      if (!strip) return uniquePlayers; // fallback, should never really happen

      const topIds = new Set([3, 4]);
      const bottomIds = new Set([1, 2]);

      return uniquePlayers.filter((p: any) => {
        const id = p.pbvPlayerId;
        if (typeof id !== "number") return false;
        if (strip === "top") return topIds.has(id);
        return bottomIds.has(id); // "bottom"
      });
    }, [uniquePlayers, strip]);

    const now = clipTimeSec ?? 0;
    const ACTIVE_WINDOW_SEC = 0.6;

    // 3) Time-based note lookup per *filtered* player
    const perPlayerNoteInfo = React.useMemo(() => {
      const info: Record<
        number,
        { activeNote: any | null; isActive: boolean }
      > = {};

      for (const player of filteredPlayers) {
        const id: number = player.pbvPlayerId;
        const notes: any[] = Array.isArray(player.techniqueNotes)
          ? player.techniqueNotes
          : [];

        let bestNote: any | null = null;
        let bestDist = Infinity;

        for (const note of notes) {
          const t =
            typeof note?.timeSecClip === "number" ? note.timeSecClip : null;
          if (t == null) continue;
          const dist = Math.abs(t - now);
          if (dist < bestDist) {
            bestDist = dist;
            bestNote = note;
          }
        }

        const isActive =
          bestNote != null && bestDist <= ACTIVE_WINDOW_SEC ? true : false;

        info[id] = { activeNote: bestNote, isActive };
      }

      return info;
    }, [filteredPlayers, now]);

    // ✅ Early return AFTER all hooks
    if (filteredPlayers.length === 0) return null;

    type CornerKey = "topLeft" | "topRight" | "bottomLeft" | "bottomRight";

    const cornerStyles: Record<CornerKey, React.CSSProperties> = {
      topLeft: {
        top: 0,
        left: 0,
        alignItems: "flex-start",
      },
      topRight: {
        top: 0,
        right: 0,
        alignItems: "flex-end",
      },
      bottomLeft: {
        bottom: 0,
        left: 0,
        alignItems: "flex-start",
      },
      bottomRight: {
        bottom: 0,
        right: 0,
        alignItems: "flex-end",
      },
    };

    const preferredCornerForId: Partial<Record<number, CornerKey>> = {
      3: "topLeft",
      4: "topRight",
      1: "bottomLeft",
      2: "bottomRight",
    };

    const allCorners: CornerKey[] = [
      "topLeft",
      "topRight",
      "bottomLeft",
      "bottomRight",
    ];

    const assignments: { player: any; corner: CornerKey }[] = [];
    const usedCorners = new Set<CornerKey>();

    for (const player of filteredPlayers) {
      let corner = preferredCornerForId[player.pbvPlayerId];
      if (!corner || usedCorners.has(corner)) {
        corner = allCorners.find((c) => !usedCorners.has(c)) ?? "topLeft";
      }
      usedCorners.add(corner);
      assignments.push({ player, corner });
    }

    const renderCardContent = (player: any) => {
      const { activeNote, isActive } = perPlayerNoteInfo[
        player.pbvPlayerId
      ] || {
        activeNote: null,
        isActive: false,
      };

      const appearanceLabel =
        player?.appearance?.shortLabel ?? `P${player.pbvPlayerId}`;

      const cue = activeNote?.shortCue ?? "";

      const description =
        activeNote?.description ||
        player?.verdictForPlayer ||
        player?.refinementOrDrillFocusForPlayer ||
        "";

      return (
        <Box
          sx={{
            borderRadius: 1,
            px: 1.5,
            py: 1,
            bgcolor: isActive ? "rgba(0,0,0,0.9)" : "rgba(0,0,0,0.6)",
            color: "#fff",
            maxWidth: 340,
            boxShadow: 3,
            border: "1px solid rgba(255,255,255,0.25)",
            fontSize: 12,
            transform: isActive ? "scale(1.02)" : "scale(0.98)",
            transition:
              "background-color 120ms ease, transform 120ms ease, opacity 120ms ease",
            opacity: description || cue ? 1 : 0.5,
          }}
        >
          <Box
            sx={{
              fontSize: 11,
              fontWeight: 600,
              opacity: 0.85,
              mb: 0.25,
              lineHeight: 1.2,
            }}
          >
            {`P${player.pbvPlayerId} · ${appearanceLabel}`}
          </Box>

          {cue && (
            <Box
              sx={{
                fontSize: 11,
                fontWeight: 600,
                mb: 0.25,
                lineHeight: 1.3,
              }}
            >
              {cue}
            </Box>
          )}

          {description && (
            <Box
              sx={{
                fontSize: 11,
                lineHeight: 1.3,
                opacity: 0.9,
              }}
            >
              {description}
            </Box>
          )}
        </Box>
      );
    };

    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        {assignments.map(({ player, corner }) => (
          <Box
            key={player.pbvPlayerId}
            sx={{
              position: "absolute",
              display: "flex",
              ...cornerStyles[corner],
              pointerEvents: "auto",
            }}
          >
            {renderCardContent(player)}
          </Box>
        ))}
      </Box>
    );
  },
);

export const TechniqueCalloutsOverlay_old = memo(
  ({
    analysis,
    clipTimeSec,
    playbackRate: _playbackRate,
    strip: _strip,
  }: TechniqueCalloutsOverlayProps) => {
    const playersRaw: any[] | undefined = analysis?.players;

    // 1) First hook – always called
    const uniquePlayers = React.useMemo(() => {
      if (!playersRaw || playersRaw.length === 0) return [];

      const byId: Record<number, any> = {};
      for (const p of playersRaw) {
        if (!p || typeof p.pbvPlayerId !== "number") continue;
        if (!byId[p.pbvPlayerId]) {
          byId[p.pbvPlayerId] = p;
        }
      }
      return Object.values(byId);
    }, [playersRaw]);

    // Time-related vars used in the second hook
    const now = clipTimeSec ?? 0;
    const ACTIVE_WINDOW_SEC = 0.6;

    // 2) Second hook – must also be called on *every* render
    const perPlayerNoteInfo = React.useMemo(() => {
      const info: Record<
        number,
        { activeNote: any | null; isActive: boolean }
      > = {};

      for (const player of uniquePlayers) {
        const id: number = player.pbvPlayerId;
        const notes: any[] = Array.isArray(player.techniqueNotes)
          ? player.techniqueNotes
          : [];

        let bestNote: any | null = null;
        let bestDist = Infinity;

        for (const note of notes) {
          const t =
            typeof note?.timeSecClip === "number" ? note.timeSecClip : null;
          if (t == null) continue;
          const dist = Math.abs(t - now);
          if (dist < bestDist) {
            bestDist = dist;
            bestNote = note;
          }
        }

        const isActive =
          bestNote != null && bestDist <= ACTIVE_WINDOW_SEC ? true : false;

        info[id] = { activeNote: bestNote, isActive };
      }

      return info;
    }, [uniquePlayers, now]);

    // ✅ This early return is now AFTER all hooks are called
    if (uniquePlayers.length === 0) return null;

    type CornerKey = "topLeft" | "topRight" | "bottomLeft" | "bottomRight";

    const cornerStyles: Record<CornerKey, React.CSSProperties> = {
      topLeft: {
        top: 0,
        left: 0,
        alignItems: "flex-start",
      },
      topRight: {
        top: 0,
        right: 0,
        alignItems: "flex-end",
      },
      bottomLeft: {
        bottom: 0,
        left: 0,
        alignItems: "flex-start",
      },
      bottomRight: {
        bottom: 0,
        right: 0,
        alignItems: "flex-end",
      },
    };

    const preferredCornerForId: Partial<Record<number, CornerKey>> = {
      3: "topLeft",
      4: "topRight",
      1: "bottomLeft",
      2: "bottomRight",
    };

    const allCorners: CornerKey[] = [
      "topLeft",
      "topRight",
      "bottomLeft",
      "bottomRight",
    ];

    const assignments: { player: any; corner: CornerKey }[] = [];
    const usedCorners = new Set<CornerKey>();

    for (const player of uniquePlayers) {
      let corner = preferredCornerForId[player.pbvPlayerId];
      if (!corner || usedCorners.has(corner)) {
        corner = allCorners.find((c) => !usedCorners.has(c)) ?? "topLeft";
      }
      usedCorners.add(corner);
      assignments.push({ player, corner });
    }

    const renderCardContent = (player: any) => {
      const { activeNote, isActive } = perPlayerNoteInfo[
        player.pbvPlayerId
      ] || {
        activeNote: null,
        isActive: false,
      };

      const appearanceLabel =
        player?.appearance?.shortLabel ?? `P${player.pbvPlayerId}`;

      const cue = activeNote?.shortCue ?? "";

      const description =
        activeNote?.description ||
        player?.verdictForPlayer ||
        player?.refinementOrDrillFocusForPlayer ||
        "";

      return (
        <Box
          sx={{
            borderRadius: 1,
            px: 1.5,
            py: 1,
            bgcolor: isActive ? "rgba(0,0,0,0.9)" : "rgba(0,0,0,0.6)",
            color: "#fff",
            maxWidth: 340,
            boxShadow: 3,
            border: "1px solid rgba(255,255,255,0.25)",
            fontSize: 12,
            transform: isActive ? "scale(1.02)" : "scale(0.98)",
            transition:
              "background-color 120ms ease, transform 120ms ease, opacity 120ms ease",
            opacity: description || cue ? 1 : 0.5,
          }}
        >
          <Box
            sx={{
              fontSize: 11,
              fontWeight: 600,
              opacity: 0.85,
              mb: 0.25,
              lineHeight: 1.2,
            }}
          >
            {`P${player.pbvPlayerId} · ${appearanceLabel}`}
          </Box>

          {cue && (
            <Box
              sx={{
                fontSize: 11,
                fontWeight: 600,
                mb: 0.25,
                lineHeight: 1.3,
              }}
            >
              {cue}
            </Box>
          )}

          {description && (
            <Box
              sx={{
                fontSize: 11,
                lineHeight: 1.3,
                opacity: 0.9,
              }}
            >
              {description}
            </Box>
          )}
        </Box>
      );
    };

    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        {assignments.map(({ player, corner }) => (
          <Box
            key={player.pbvPlayerId}
            sx={{
              position: "absolute",
              display: "flex",
              ...cornerStyles[corner],
              pointerEvents: "auto",
            }}
          >
            {renderCardContent(player)}
          </Box>
        ))}
      </Box>
    );
  },
);

export default App;
